/**
 * DiscussionWindow.tsx — Zone Discussion V3 (système unifié)
 *
 * Architecture:
 *   1. Header h-12 UB_BLUE — avatar bot actif, roster, AgentSelector (+)
 *   2. Messages — V3MessageList (rendu natif V3: bulles colorées par bot,
 *      options cliquables, streaming cursor, thinking steps, coaching sentinelle)
 *   3. Input — ChatBoxV3 (texte, voice call LiveKit, upload fichier)
 *   4. Accueil — DeptWelcomeScreen quand la discussion est vide
 *
 * Pipeline unique:
 *   AmorcerContext (activeBotCode) → BotCodeSync → FrameMasterContext
 *   ChatBoxV3 envoie sendMessage(text, activeBotCode) via ChatContext
 *   V3MessageList lit messages via ChatContext (même source unique)
 *
 * ZERO LiveChat V2. UN SEUL système de rendu. Pas de spaghetti.
 */

import { useState, useRef, useEffect, useCallback, type KeyboardEvent, type ChangeEvent } from "react";
import {
  Bot, BrainCog, Atom, Plus, Send, ChevronUp, X, Pin, Check, CheckCircle2, ChevronDown, ChevronRight,
  Phone, PhoneOff, Video, Glasses, Paperclip, Globe, Zap, Activity, Users,
  Brain, Target, AlertTriangle, Scale, Sparkles, MessageSquare,
  Mic, MicOff, Loader2, Upload, MessageCircle, Clock, Network, Pencil,
  BookOpen, Search, BarChart2, Lightbulb, FileText, Hammer,
  Eye, Swords, Shield, Crown, type LucideIcon,
  UserPlus, GraduationCap, Handshake, RotateCcw, Presentation, Timer, DollarSign, UserCheck,
} from "lucide-react";
import { cn } from "../components/ui/utils";
import { IntegrationsPanel } from "./IntegrationsPanel";
import { ChantierSuggestionModal } from "./components/ChantierSuggestionModal";
import { useAmorcer } from "./AmorcerContext";
import { useDemo } from "./DemoContext";
import { DemoChatPlayer } from "./DemoChatPlayer";
import { useChatContext } from "../v2/context/ChatContext";
import { BOT_AVATAR, BOT_NAME, BOT_ROLE, type AutoConsultationData, type ReflectionMode } from "../v2/api/types";
import { useIsMobile } from "../components/ui/use-mobile";
import { api } from "../v2/api/client";
import type { TeamSuggestionEvent } from "../v2/api/client";
import { BOT_CODES } from "./constants";
import { DEPT_DASH_ICON, DEPT_GRADIENT, BOT_DISPLAY, PHASE_COLORS } from "./sections/shared/dept-data";
import { DEPT_GREETING, DEPT_ACTIONS, ACTION_COLORS } from "./data/dept-welcome";
import type { PhaseKey, TaskItem } from "./core/types";
import { PHASE_CONFIG } from "./core/phases";
import { detectPhaseFromMessage } from "./core/phase-router";
import { useDiscussionElements, EVT_RELIRE_ELEMENTS } from "./hooks/useDiscussionElements";
import { nomEnCours } from "./hooks/nomDecideur";  // P-S232-39 Q3 : la carte « Qui approuve » ne repose pas la question pendant la saisie du nom
import { EVT_BULLE_POSER_QUESTION, EVT_MESSAGE_ECRAN, signalerEcran, choixDuChat, executerChoixGeste, actionDuChoix, enregistrerChoixBulles, boutonsDeBulle, bulleAuGesteEnAttente } from "./hooks/useGestesDiscussion";
import { demanderChantierAOuvrir } from "./hooks/useHierarchieChantiers";
import { CarteAction, cliquerBoutonCarte, EVT_FOCUS_DISCUSSION } from "./CarteAction";  // S231 lot Z2 (ZONES.CONNECTEES.1)
// getContextualActions retire — remplace par footer 2 niveaux (Bible Live 4.14)
import { getPhaseSteps } from "./phases/phase-config";
import { buildExpertContext } from "./phases/LiveDiscussionView";
import { BubbleActions } from "./phases/BubbleActions";
import { phaseEnvoyee } from "./phases/etape-greffier";
import { detectBlockTypeFrontend, extractStructuredDataFrontend, extractTitle, generateExpertBlockTitle } from "./hooks/useWorkspaceCapture";
import {
  Room, RoomEvent, Track,
  type RemoteTrack, type RemoteTrackPublication,
  type Participant, type DisconnectReason,
} from "livekit-client";


// ═══ V3 BUBBLE PALETTE — couleurs par bot (1 source de vérité) ═══
const V3_STYLE: Record<string, { text: string; border: string; ring: string; bubble: string }> = {
  CEOB:  { text: "text-blue-700",    border: "border-blue-400",    ring: "ring-blue-300",    bubble: "bg-blue-50/40" },
  CTOB:  { text: "text-violet-700",  border: "border-violet-400",  ring: "ring-violet-300",  bubble: "bg-violet-50/40" },
  CFOB:  { text: "text-emerald-700", border: "border-emerald-400", ring: "ring-emerald-300", bubble: "bg-emerald-50/40" },
  CMOB:  { text: "text-pink-700",    border: "border-pink-400",    ring: "ring-pink-300",    bubble: "bg-pink-50/40" },
  CSOB:  { text: "text-red-700",     border: "border-red-400",     ring: "ring-red-300",     bubble: "bg-red-50/40" },
  COOB:  { text: "text-orange-700",  border: "border-orange-400",  ring: "ring-orange-300",  bubble: "bg-orange-50/40" },
  CPOB:  { text: "text-slate-700",   border: "border-slate-400",   ring: "ring-slate-300",   bubble: "bg-slate-50/40" },
  CHROB: { text: "text-teal-700",    border: "border-teal-400",    ring: "ring-teal-300",    bubble: "bg-teal-50/40" },
  CINOB: { text: "text-rose-700",    border: "border-rose-400",    ring: "ring-rose-300",    bubble: "bg-rose-50/40" },
  CROB:  { text: "text-amber-700",   border: "border-amber-400",   ring: "ring-amber-300",   bubble: "bg-amber-50/40" },
  CLOB:  { text: "text-indigo-700",  border: "border-indigo-400",  ring: "ring-indigo-300",  bubble: "bg-indigo-50/40" },
  CISOB: { text: "text-zinc-700",    border: "border-zinc-400",    ring: "ring-zinc-300",    bubble: "bg-zinc-50/40" },
};
const DEFAULT_STYLE = V3_STYLE.CEOB;
// DEC-OPTIONS-SCENARIO-1 : quatrième option d'une bulle qui pose une question du scénario (texte du skin, registre/skins/manufacturier_r1.json)
const OPTION_JE_NE_SAIS_PAS = "Je ne sais pas.";
/** P-S232-45 (DEC-DEROULEMENT-OPTIONS-1) : une bulle qui porte une option de déroulement (données du skin, posées par le serveur après ses
 *  options) garde jusqu'à 5 options : « Creuser la piste 1 », « Creuser la piste 2 », « Creuser la piste 3 », « Je suis prêt à choisir »… */
const RE_OPTION_DEROULEMENT = /^(Continuer à cerner|Passer aux pistes|Creuser la piste \d+|Comparer les pistes|Je suis prêt à choisir|Explorer encore avant le plan|Préparer le plan de match)$/;

/** Inline formatting: bold, italic, code — copié du V2 LiveChat (source de vérité) */
function applyInlineFormatting(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="text-gray-600 italic">$1</em>')
    .replace(/`(.+?)`/g, '<code class="px-1 py-0.5 bg-gray-100 rounded text-xs font-mono text-gray-800">$1</code>')
    .replace(/\[DONNEE REQUISE:\s*([^\]]+)\]/gi, '<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium border border-amber-200">⚠ Donnée manquante: $1</span>');
}

/** Rich markdown → HTML — copié du V2 formatBotText (source de vérité) ; exporté pour son test (S230 lot V) */
export function formatMarkdown(text: string): string {
  if (!text) return "";
  // Strip internal labels: [INTERNE...], [EQUIPE PRESENTE:...], [BOT PRINCIPAL], [CONSULTANT — ...]
  const stripped = text
    .split("\n")
    .filter(l => !/^\[(INTERNE|EQUIPE PRESENTE|BOT PRINCIPAL|CONSULTANT\s*[—\-])[^\]]*\]/i.test(l.trim()))
    .join("\n");
  let html = stripped
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  const lines = html.split("\n");
  const result: string[] = [];
  let listTag: "ul" | "ol" | null = null;  // Track open list type
  let inCodeBlock = false;
  let codeBlockLang = "";
  let codeBlockLines: string[] = [];
  let tableLines: string[] = [];

  const closeList = () => {
    if (listTag) { result.push(`</${listTag}>`); listTag = null; }
  };

  const flushTable = () => {
    if (tableLines.length === 0) return;
    const rows = tableLines.map(r => r.replace(/^\|/, "").replace(/\|$/, "").split("|").map(c => c.trim()));
    // Detect separator row (---|---) to split header from body
    let headerEnd = -1;
    for (let r = 0; r < rows.length; r++) {
      if (rows[r].every(c => /^[-:]+$/.test(c))) { headerEnd = r; break; }
    }
    let html = '<div class="my-2"><table class="text-sm border-collapse w-full">';
    for (let r = 0; r < rows.length; r++) {
      if (headerEnd >= 0 && r === headerEnd) continue; // skip separator
      const isHead = headerEnd > 0 && r < headerEnd;
      const tag = isHead ? "th" : "td";
      const cls = isHead
        ? 'class="px-3 py-1.5 text-left font-semibold text-gray-900 border-b border-gray-300 bg-gray-50"'
        : 'class="px-3 py-1.5 text-left text-gray-700 border-b border-gray-100"';
      html += "<tr>" + rows[r].map(c => `<${tag} ${cls}>${applyInlineFormatting(c)}</${tag}>`).join("") + "</tr>";
    }
    html += "</table></div>";
    result.push(html);
    tableLines = [];
  };

  for (let i = 0; i < lines.length; i++) {
    let line = lines[i];

    // Code blocks: ``` or ```language
    if (line.trim().startsWith("```")) {
      if (!inCodeBlock) {
        closeList();
        inCodeBlock = true;
        codeBlockLang = line.trim().replace(/^```/, "").trim();
        codeBlockLines = [];
        continue;
      } else {
        // Closing code block
        inCodeBlock = false;
        const langLabel = codeBlockLang ? `<div class="text-[10px] text-gray-400 mb-1 font-mono">${codeBlockLang}</div>` : "";
        result.push(`<div class="my-2 rounded-lg bg-gray-900 text-gray-100 p-3"><pre class="text-xs font-mono whitespace-pre-wrap break-all leading-relaxed">${langLabel}${codeBlockLines.join("\n")}</pre></div>`);
        codeBlockLines = [];
        codeBlockLang = "";
        continue;
      }
    }

    if (inCodeBlock) {
      codeBlockLines.push(line);
      continue;
    }

    // Horizontal rules: --- ━━━ ═══
    if (/^[━─═\-]{3,}$/.test(line.trim())) {
      closeList();
      result.push('<hr class="my-3 border-gray-200">');
      continue;
    }

    // Inline option chips: "1. text | 2. text" or "1 · text | 2 · text"
    if (/^\p{Emoji}?\s*\d+\s*[·.]\s*.+\|/u.test(line.trim())) {
      closeList();
      const options = line.split(/\s*\|\s*/);
      result.push('<div class="flex flex-wrap gap-2 my-3">');
      for (const opt of options) {
        const cleaned = opt.replace(/^\p{Emoji}?\s*\d+\s*[·.]\s*/u, "").trim();
        if (cleaned) result.push(`<span class="inline-flex items-center px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium">${cleaned}</span>`);
      }
      result.push("</div>");
      continue;
    }

    // Headers: ### or ## (avec ou sans espace après le #)
    if (/^#{1,3}\s*\S/.test(line.trim())) {
      closeList();
      const hText = applyInlineFormatting(line.trim().replace(/^#{1,3}\s*/, ""));
      result.push(`<div class="font-semibold text-gray-900 mt-3 mb-1">${hText}</div>`);
      continue;
    }

    // Numbered list: 1. 2) etc
    const numberedMatch = line.match(/^(\s*)(\d+)[.)]\s+(.+)/);
    if (numberedMatch) {
      if (listTag !== "ol") { closeList(); result.push('<ol class="space-y-1.5 my-2 list-none">'); listTag = "ol"; }
      const content = applyInlineFormatting(numberedMatch[3]);
      result.push(`<li class="flex items-start gap-2 text-sm"><span class="text-gray-400 mt-0.5 shrink-0 font-semibold">${numberedMatch[2]}.</span><span>${content}</span></li>`);
      continue;
    }

    // Bullet list: * - • or emoji bullets
    // S230 lot V (o1fs 3523, « 5 » / « à 10 heures ») : \p{Emoji} acceptait les chiffres 0-9 comme puce ; \p{Extended_Pictographic} ne les prend pas
    const bulletMatch = line.match(/^(\s*)([-*•]|\p{Emoji_Presentation}|\p{Extended_Pictographic}\uFE0F?)\s+(.+)/u);
    if (bulletMatch) {
      if (listTag !== "ul") { closeList(); result.push('<ul class="space-y-1.5 my-2">'); listTag = "ul"; }
      const content = applyInlineFormatting(bulletMatch[3]);
      const emoji = /^[-*•]$/.test(bulletMatch[2]) ? "" : bulletMatch[2] + " ";
      result.push(`<li class="flex items-start gap-2 text-sm"><span class="text-gray-400 mt-0.5 shrink-0">${emoji || "•"}</span><span>${content}</span></li>`);
      continue;
    }

    // Close list if we hit a non-list line
    if (listTag && line.trim() !== "") {
      closeList();
    }

    // Table rows: lines starting with |
    if (/^\s*\|/.test(line)) {
      closeList();
      tableLines.push(line.trim());
      continue;
    }
    // Flush table if we exit table lines
    if (tableLines.length > 0) {
      flushTable();
    }

    // Empty line = spacing
    if (line.trim() === "") {
      result.push('<div class="h-2"></div>');
      continue;
    }

    // Bold-only line = header
    if (/^\*\*(.+)\*\*\s*:?\s*$/.test(line.trim())) {
      const headerText = line.trim().replace(/^\*\*(.+)\*\*\s*:?\s*$/, "$1");
      result.push(`<div class="font-semibold text-gray-900 mt-3 mb-1">${headerText}</div>`);
      continue;
    }

    // Regular paragraph
    result.push(`<p class="text-sm leading-relaxed">${applyInlineFormatting(line)}</p>`);
  }

  closeList();
  flushTable();
  // Flush any unclosed code block (e.g. during streaming)
  if (inCodeBlock && codeBlockLines.length > 0) {
    const langLabel = codeBlockLang ? `<div class="text-[10px] text-gray-400 mb-1 font-mono">${codeBlockLang}</div>` : "";
    result.push(`<div class="my-2 rounded-lg bg-gray-900 text-gray-100 p-3"><pre class="text-xs font-mono whitespace-pre-wrap break-all leading-relaxed">${langLabel}${codeBlockLines.join("\n")}</pre></div>`);
  }
  return result.join("\n");
}

// ═══ SEGMENTATION & CRISTALLISATION MANUELLE (phase discussion) ═══

/** Parse un message bot en segments par headers ### */
function parseMessageSegments(content: string): { title: string | null; text: string }[] {
  if (!content) return [];
  const parts = content.split(/(?=^#{1,4}\s)/m);
  const segments: { title: string | null; text: string }[] = [];
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const headerMatch = trimmed.match(/^#{1,4}\s+(.+?)(?:\n|$)/);
    if (headerMatch) {
      segments.push({
        title: headerMatch[1].trim(),
        text: trimmed.replace(/^#{1,4}\s+.+?\n?/, "").trim(),
      });
    } else {
      segments.push({ title: null, text: trimmed });
    }
  }
  return segments;
}

/** Card contribution secondaire — style ConferenceAI (avatar + role badge + contenu complet + collapse) */
function ContributionCard({ agent, nom, contenu, style }: {
  agent: string; nom: string; contenu: string;
  style: { text: string; border: string; ring: string; bubble: string };
}) {
  const [expanded, setExpanded] = useState(false);
  const safe = contenu || "";
  const isLong = safe.length > 300;

  return (
    <div className={cn("border-l-[3px] rounded-lg rounded-tl-none px-3 py-2", style.border, "bg-white/60")}>
      {/* Header: avatar + nom + role badge pill */}
      <div className="flex items-center gap-2 mb-1">
        <div className={cn("w-5 h-5 rounded-full overflow-hidden shrink-0 ring-1", style.ring)}>
          <img src={BOT_AVATAR[agent] || `/agents/${agent.toLowerCase()}.png`}
            alt="" className="w-full h-full object-cover" />
        </div>
        <span className={cn("text-[11px] font-bold", style.text)}>{nom}</span>
        <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full font-medium", style.bubble, style.text)}>
          {BOT_ROLE[agent] || "Consultant"}
        </span>
      </div>
      {/* Contenu complet avec markdown — scroll au lieu de tronquer */}
      <div className={cn(
        "text-[12px] text-gray-700 leading-relaxed",
        isLong && !expanded && "max-h-[200px] overflow-y-auto"
      )}
        dangerouslySetInnerHTML={{ __html: formatMarkdown(safe) }} />
      {/* Toggle expand/collapse */}
      {isLong && (
        <button onClick={() => setExpanded(!expanded)}
          className="text-[10px] text-blue-600 hover:text-blue-800 mt-1 cursor-pointer font-medium">
          {expanded ? "▲ Voir moins" : "▼ Voir la réponse complète"}
        </button>
      )}
    </div>
  );
}

// P-S232-43 D8 (Carl, 4 nov. 20 h 17 : « pour quand Carlos réfléchit, on a juste à mettre plus de textes de raisonnement ») : pendant l'attente,
// l'étape dite par le serveur, puis des textes de raisonnement qui défilent toutes les 2,2 s — sans chiffre ni fait inventé
const TEXTES_REFLEXION = ["relit ce que vous avez dit…", "repère ce qui est établi et ce qui manque…", "compare les pistes possibles…",
  "vérifie de ne rien avancer qui n'ait été dit…", "prépare sa réponse…"];
function EtapeReflexion({ etape }: { etape: string }) {
  const [tick, setTick] = useState(0);
  useEffect(() => { setTick(0); const t = setInterval(() => setTick((p) => p + 1), 2200); return () => clearInterval(t); }, [etape]);
  const texte = tick === 0 ? etape : TEXTES_REFLEXION[(tick - 1) % TEXTES_REFLEXION.length];
  return <span className="text-gray-500 animate-in fade-in duration-500" key={texte} data-etape-reflexion>{texte}</span>;
}

/** S109 — Label dynamique par bot dans la bulle multi-thinking (etapes variees contextuelles) */
function ThinkingLabel({ botCode, userText }: { botCode: string; userText?: string }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((p) => p + 1), 1800);
    return () => clearInterval(t);
  }, []);

  const STOPS = new Set([
    "dans","pour","avec","comment","quel","quelle","cette","votre","notre",
    "quels","quelles","faire","faut","veux","voudrais","aimerais","peux",
    "peut","dois","doit","aussi","encore","comme","juste","vraiment",
    "toujours","suis","sont","être","etre","avoir","tout","tous",
    "mais","puis","donc","alors","meme","tres","plus","moins",
  ]);
  const kw = userText
    ? userText.replace(/[?!.,;:'"()]/g, "").split(" ")
        .filter((w) => w.length > 3 && !STOPS.has(w.toLowerCase()))
        .slice(0, 2).join(" ")
    : "";

  const ANGLES: Record<string, [string, string]> = {
    CEOB: ["Vision strategique", "Priorites"], CFOB: ["Modelisation", "Projection"],
    CMOB: ["Positionnement", "Acquisition"], CSOB: ["Risques", "Scenarios"],
    CTOB: ["Architecture", "Faisabilite"], COOB: ["Operations", "Execution"],
    CPOB: ["Chaine valeur", "Flux"], CHROB: ["Equipe", "Talent"],
    CINOB: ["Tendances", "Benchmark"], CROB: ["Pipeline", "Conversion"],
    CLOB: ["Conformite", "Juridique"], CISOB: ["Securite", "Protection"],
  };

  const [a1, a2] = ANGLES[botCode] || ["Analyse", "Evaluation"];
  const phases = kw
    ? [kw, a1, "Enjeux", a2, "Formulation"]
    : [a1, "Contexte", a2, "Evaluation", "Formulation"];
  const label = phases[tick % phases.length];

  return (
    <span className="text-[9px] text-gray-400 text-center truncate max-w-[80px]">
      {label}...
    </span>
  );
}

/** ExpertSuggestionChips — PR bubbles dans le fil de discussion pour consultation directe */
function ExpertSuggestionChips({ suggestions, onConsult }: {
  suggestions: Array<{ consult: string; consult_nom: string; consult_emoji: string; consult_titre?: string; reason: string }>;
  onConsult: (s: { consult: string; consult_nom: string; reason: string }) => void;
}) {
  const [loading, setLoading] = useState<string | null>(null);
  const [done, setDone] = useState<Set<string>>(new Set());

  const handleClick = (s: typeof suggestions[0]) => {
    if (loading || done.has(s.consult)) return;
    setLoading(s.consult);
    onConsult(s);
    // Marquer comme fait après 800ms (le bot commence à répondre)
    setTimeout(() => { setLoading(null); setDone(prev => new Set([...prev, s.consult])); }, 800);
  };

  return (
    <div className="mt-3 pt-2 border-t border-gray-100 space-y-1.5">
      <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide flex items-center gap-1">
        <Users className="h-2.5 w-2.5" /> Input d'expert recommandé
      </span>
      {suggestions.map((s, i) => {
        const isDone = done.has(s.consult);
        const isLoading = loading === s.consult;
        return (
          <button
            key={`expert-${i}`}
            onClick={() => handleClick(s)}
            disabled={!!loading || isDone}
            className={cn(
              "w-full flex items-center gap-2.5 px-3 py-2 rounded-xl border text-left transition-all",
              isDone
                ? "border-emerald-200 bg-emerald-50/60 cursor-default"
                : "border-blue-100 bg-gradient-to-r from-blue-50/60 to-white hover:border-blue-300 hover:shadow-sm cursor-pointer group"
            )}
          >
            <div className={cn("w-7 h-7 rounded-full overflow-hidden shrink-0 ring-1 transition-all",
              isDone ? "ring-emerald-300" : "ring-blue-200 group-hover:ring-blue-400"
            )}>
              <img
                src={BOT_AVATAR[s.consult] || `/agents/${s.consult?.toLowerCase()}.png`}
                alt={s.consult_nom}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-1.5">
                <span className={cn("text-[11px] font-bold", isDone ? "text-emerald-700" : "text-blue-700")}>
                  {s.consult_nom}
                </span>
                {s.consult_titre && (
                  <span className="text-[9px] text-gray-400 truncate">{s.consult_titre}</span>
                )}
              </div>
              <p className="text-[10px] text-gray-500 leading-snug line-clamp-1">{s.reason}</p>
            </div>
            <div className={cn(
              "shrink-0 px-2 py-1 rounded-lg text-[9px] font-bold transition-colors flex items-center gap-1",
              isDone
                ? "bg-emerald-100 text-emerald-700"
                : "bg-blue-600 text-white group-hover:bg-blue-700"
            )}>
              {isLoading ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : isDone ? (
                <><CheckCircle2 className="h-3 w-3" /> Ajouté</>
              ) : (
                "Input →"
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/** AutoConsultationPills — N2/N10/N11/N12 fix: couleurs bot, markdown, option = capture (no bifurcation) */
function AutoConsultationPills({
  consultations,
  onAddToWorkspace,
}: {
  consultations: AutoConsultationData[];
  onAddToWorkspace: (c: AutoConsultationData, selectedOption?: string) => void;
}) {
  const [expanded, setExpanded] = useState<number | null>(null);
  const [selected, setSelected] = useState<Record<number, string>>({});
  const [added, setAdded] = useState<Set<number>>(new Set());

  return (
    <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
      <span className="text-[9px] text-gray-500 font-medium uppercase tracking-wide flex items-center gap-1">
        <Users className="h-2.5 w-2.5" /> Consultations d'experts
      </span>
      {consultations.map((c, i) => {
        const cStyle = V3_STYLE[c.bot_code] || DEFAULT_STYLE;
        const isExpanded = expanded === i;
        const chosenLabel = selected[i];
        return (
          <div key={`ac-${i}`} className={cn("rounded-lg border overflow-hidden", cStyle.border)}>
            <button
              onClick={() => setExpanded(isExpanded ? null : i)}
              className={cn("w-full flex items-center gap-2 px-3 py-1.5 transition-colors text-left hover:brightness-95", cStyle.bubble)}
            >
              <img
                src={BOT_AVATAR[c.bot_code] || `/agents/${c.bot_code?.toLowerCase()}.png`}
                alt={c.bot_nom}
                className="w-5 h-5 rounded-full object-cover shrink-0"
              />
              <span className={cn("text-[10px] font-bold flex-1 truncate", cStyle.text)}>{c.bot_nom}</span>
              {chosenLabel ? (
                <span className="text-[9px] text-emerald-600 flex items-center gap-0.5 shrink-0">
                  <CheckCircle2 className="h-2.5 w-2.5" /> {chosenLabel}
                </span>
              ) : (
                <span className="text-[9px] text-gray-400 line-clamp-1 flex-1 truncate">{c.reason}</span>
              )}
              <ChevronDown className={cn("h-3 w-3 text-gray-400 shrink-0 transition-transform", isExpanded && "rotate-180")} />
            </button>
            {isExpanded && (
              <div className="px-3 py-2 bg-white border-t border-gray-100 space-y-2">
                {/* N11: contenu formaté markdown, N2: plus de line-clamp */}
                <div
                  className="text-sm leading-relaxed text-gray-700 [&>p]:my-1 [&>ul]:my-1.5 [&>ol]:my-1.5 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0"
                  dangerouslySetInnerHTML={{ __html: formatMarkdown(c.contenu) }}
                />
                {c.options && c.options.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1 border-t border-gray-50">
                    <span className="text-[9px] text-gray-400 w-full mb-0.5">Point retenu :</span>
                    {c.options.map((opt, j) => {
                      const isChosen = selected[i] === opt.label;
                      return (
                        <button
                          key={j}
                          onClick={() => {
                            // N12/N3/N4: capture le choix dans la pill, sans envoyer de message (no bifurcation)
                            setSelected(prev => ({ ...prev, [i]: isChosen ? "" : opt.label }));
                            if (!isChosen) setExpanded(null);
                          }}
                          className={cn(
                            "flex items-center gap-1 px-2 py-0.5 rounded text-[9px] border transition-colors cursor-pointer",
                            isChosen
                              ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                              : "border-gray-200 bg-gray-50 text-gray-600 hover:bg-gray-100"
                          )}
                        >
                          {isChosen ? <Check className="h-2.5 w-2.5" /> : <ChevronRight className="h-2.5 w-2.5" />}
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                )}
                <button
                  onClick={() => {
                    if (added.has(i)) return;
                    onAddToWorkspace(c, selected[i] || undefined);
                    setAdded(prev => new Set([...prev, i]));
                    setExpanded(null);
                  }}
                  className={cn(
                    "flex items-center gap-1 px-2 py-0.5 rounded text-[9px] border transition-colors cursor-pointer",
                    added.has(i)
                      ? "bg-emerald-50 border-emerald-300 text-emerald-700 cursor-default"
                      : cn("bg-white hover:bg-gray-50", cStyle.border, cStyle.text)
                  )}
                >
                  {added.has(i)
                    ? <><CheckCircle2 className="h-2.5 w-2.5" /> Ajouté</>
                    : <><Plus className="h-2.5 w-2.5" /> +Workspace</>
                  }
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** CascadeChips — small chips in bubble footer for cascade/reflexion actions */
function CascadeChips({ suggestions, onAction }: {
  suggestions: Array<{ target_section: string; message: string; view: string; sub_section: string }>;
  onAction: (s: { message: string; view: string; target_section: string }) => void;
}) {
  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide flex items-center gap-1">
        <Zap className="h-2.5 w-2.5" /> Actions suggérées
      </span>
      <div className="flex flex-wrap gap-1 mt-1">
        {suggestions.map((s, i) => (
          <button
            key={`cascade-${i}`}
            onClick={() => onAction(s)}
            className="flex items-center gap-1 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 text-[10px] text-violet-700 hover:bg-violet-100 hover:border-violet-300 cursor-pointer transition-all font-medium"
          >
            <Brain className="h-2.5 w-2.5 shrink-0" />
            <span>{s.message || s.target_section}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

/** TeamSuggestionChip — bloc compact footer bulle: phrase punch + bouton Accepter/Ignorer */
function TeamSuggestionChip({ suggestion, onAdd }: {
  suggestion: TeamSuggestionEvent;
  onAdd: (botCode: string) => void;
}) {
  const [added, setAdded] = useState(false);
  const [ignored, setIgnored] = useState(false);
  const botCode = suggestion.bot_code;
  const s = V3_STYLE[botCode] || DEFAULT_STYLE;
  // C.54 — Masquer après Ignorer
  if (ignored) return null;
  return (
    <div className="mt-2 pt-2 border-t border-gray-100">
      <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide flex items-center gap-1">
        <Users className="h-2.5 w-2.5" /> Suggestion d'équipe
      </span>
      <div className="flex items-center gap-2.5 mt-1.5 bg-gradient-to-r from-indigo-50/60 to-white border border-indigo-100 rounded-xl px-3 py-2">
        <div className={cn("w-7 h-7 rounded-full overflow-hidden shrink-0 ring-2", s.ring)}>
          <img src={BOT_AVATAR[botCode] || `/agents/${botCode.toLowerCase()}.png`} alt={suggestion.bot_nom} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-1.5">
            <span className={cn("text-[11px] font-bold shrink-0", s.text)}>{suggestion.bot_nom}</span>
            <span className="text-[9px] text-gray-400 truncate">{suggestion.bot_titre}</span>
          </div>
          <p className="text-[11px] text-gray-600 leading-snug mt-0.5 line-clamp-2">«&nbsp;{suggestion.pitch}&nbsp;»</p>
        </div>
        {/* C.54 — Boutons Accepter + Ignorer */}
        {!added ? (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => { onAdd(botCode); setAdded(true); }}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold border border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50 hover:border-indigo-300 cursor-pointer transition-all"
            >
              <UserPlus className="h-3 w-3" /> Accepter
            </button>
            <button
              onClick={() => setIgnored(true)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold border border-gray-200 bg-white text-gray-400 hover:bg-gray-50 hover:text-gray-600 cursor-pointer transition-all"
            >
              <X className="h-3 w-3" /> Ignorer
            </button>
          </div>
        ) : (
          <span className="shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold bg-green-50 border border-green-200 text-green-700">
            <CheckCircle2 className="h-3 w-3" /> Ajouté
          </span>
        )}
      </div>
    </div>
  );
}

// CristalliseBar removed — cristallisation is now automatic via useWorkspaceCapture

/**
 * C.49 — crystallizeAndAddBlock: chemin central normalisé pour tous les blocs workspace.
 * Applique detectBlockTypeFrontend + extractStructuredDataFrontend (qualité niveau 3, sync, zéro latence).
 * Tous les chemins manuels (handleOption, addExpertToWorkspace, AutoConsultationPills, etc.) passent ici.
 */
function crystallizeAndAddBlock(
  content: string,
  opts: {
    addWorkspaceBlock: (block: import("./core/types").WorkspaceBlock) => void;
    botCode: string;
    credoStep?: "C" | "R" | "E" | "D" | "O";
    sectionId?: string;
    sourceType?: "chat" | "voice" | "meeting";
    forceType?: import("./core/types").WorkspaceBlockType;
    forceTitle?: string;
    confidence?: number;
    credo_sub_section?: string;
    blockId?: string;
    replace_block_id?: string;
    merge_label?: string;
    backendBlock?: import("./core/types").WorkspaceBlock;
  }
): void {
  const _bb = opts.backendBlock;
  const type = opts.forceType || _bb?.type || detectBlockTypeFrontend(content);
  const structured_data = _bb?.structured_data || extractStructuredDataFrontend(content, type);
  const title = opts.forceTitle || _bb?.title || extractTitle(content);
  const summary = _bb?.summary || (content.length > 600 ? content.substring(0, 597) + "…" : content);
  opts.addWorkspaceBlock({
    id: opts.blockId || `blk-${Date.now()}`,
    type,
    title,
    summary,
    structured_data,
    credo_step: opts.credoStep || "C",
    confidence: opts.confidence ?? 0.75,
    source: opts.botCode,
    sourceType: opts.sourceType || "chat",
    sectionId: opts.sectionId,
    credo_sub_section: opts.credo_sub_section,
    replace_block_id: opts.replace_block_id,
    merge_label: opts.merge_label,
    timestamp: Date.now(),
  });
}

/** Helper: cristallisation intelligente via API (Cristalliser vers [Section]) */
async function cristalliseViaAPI(
  content: string,
  opts: {
    botCode: string;
    sectionId: string;
    credoStep: "C" | "R" | "E" | "D" | "O";
    userMsg?: string;
    addWorkspaceBlock: (block: import("./core/types").WorkspaceBlock) => void;
  },
) {
  const { botCode, sectionId, credoStep, userMsg, addWorkspaceBlock } = opts;
  try {
    const _jwt = (() => { try { return localStorage.getItem("ghostx-jwt"); } catch { return null; } })();
    const res = await fetch("/api/v1/workspace/cristallise", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(_jwt ? { "Authorization": `Bearer ${_jwt}` } : {}) },
      body: JSON.stringify({
        content,
        user_msg: userMsg || "",
        credo_phase: credoStep,
      }),
    });
    const block = await res.json();
    addWorkspaceBlock({
      id: `blk-${Date.now()}`,
      type: block.type || "libre",
      title: block.title || content.substring(0, 60),
      summary: block.summary || content.substring(0, 300),
      structured_data: { ...block.structured_data, originalContent: content },
      credo_step: credoStep,
      confidence: block.confidence || 0.5,
      source: botCode,
      sourceType: "chat",
      sectionId,
      timestamp: Date.now(),
    });
  } catch {
    // C.49 — Fallback normalisé via crystallizeAndAddBlock (qualité frontend homogène)
    crystallizeAndAddBlock(content, {
      addWorkspaceBlock,
      botCode,
      credoStep,
      sectionId,
      confidence: 0.6,
    });
  }
}

/** Contenu bot segmenté — sous-bulles (cristallisation automatique via useWorkspaceCapture) */
function SegmentedBotContent({ content }: {
  content: string;
  botCode: string;
  activePhase: string;
  addWorkspaceBlock: (block: import("./core/types").WorkspaceBlock) => void;
  lastUserMessage?: string;
}) {
  const segments = parseMessageSegments(content);

  // Pas de segmentation (1 seul bloc sans titre) → rendu normal
  if (segments.length <= 1) {
    return (
      <div
        className="text-sm text-gray-700 leading-relaxed isolate overflow-hidden [&>p]:my-0.5 [&>ul]:my-1 [&>ol]:my-1 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&>hr]:my-2 [&_li]:break-words [&_p]:break-words"
        dangerouslySetInnerHTML={{ __html: formatMarkdown(content) }}
      />
    );
  }

  // Segments multiples → sous-bulles
  return (
    <div className="space-y-2">
      {segments.map((seg, i) => (
        <div key={i} className="rounded-lg bg-white/60 border border-gray-100 px-3 py-2">
          {seg.title && (
            <div className="text-xs font-semibold text-gray-800 mb-1">{seg.title}</div>
          )}
          <div
            className="text-sm text-gray-700 leading-relaxed [&>p]:my-0.5 [&>ul]:my-1 [&>ol]:my-1"
            dangerouslySetInnerHTML={{ __html: formatMarkdown(seg.text) }}
          />
        </div>
      ))}
    </div>
  );
}

// ═══ InlineOptions — Checkboxes visibles + multi-select natif + réponse inline questions ═══

function InlineOptions({ options, onSend, isActive, msgType, agent, activeRoster, workspacePhase, fil, versionBulles }: {
  options: string[];
  onSend: (text: string, targetBot?: string, meta?: unknown, extra?: { workspacePhase?: string }) => void;
  isActive: boolean;
  msgType?: string;
  agent?: string;
  activeRoster: string[];
  workspacePhase?: string;
  /** S229 lot 7e (P5) : le fil, pour les gestes portés par les bulles du code ; versionBulles redessine quand ils sont lus */
  fil?: string | null;
  versionBulles?: number;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [inlineTexts, setInlineTexts] = useState<Record<number, string>>({});
  const [precisionTexts, setPrecisionTexts] = useState<Record<number, string>>({});
  const [globalPrecision, setGlobalPrecision] = useState("");
  const [showInputFor, setShowInputFor] = useState<number | null>(null);

  // Strip markdown résiduel (** et *) + filtrer "Ouvrir l'atelier"
  const stripMd = (s: string) => s.replace(/\*{1,3}([^*]+)\*{1,3}/g, '$1').replace(/\*/g, '').trim();
  const filteredOpts = options.map(stripMd).filter(opt => opt && !/ouvrir\s+l'atelier/i.test(opt));
  if (filteredOpts.length === 0) return null;
  // S229 lot 7e (P5, E7.dc.html:19) : la bulle de fin de plan a deux boutons (marteau, document), classes de la maquette ; un clic part
  // directement (geste porté par la bulle, sans message) ; les autres options du chat ne changent pas
  void versionBulles;
  const boutonsE7 = boutonsDeBulle(fil, filteredOpts);
  if (boutonsE7) {
    const STYLE_E7: Record<string, [string, LucideIcon]> = {
      creer: ["text-xs bg-indigo-50 text-indigo-700 border border-indigo-200 px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5", Hammer],
      ajuster: ["text-xs bg-gray-50 text-gray-700 border border-gray-200 px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5", FileText],
      // P-S232-45 (DEC-DEROULEMENT-OPTIONS-1) : « Explorer encore avant le plan » — même pilule grise que « Ajuster d'abord », icône loupe
      reporter: ["text-xs bg-gray-50 text-gray-700 border border-gray-200 px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5", Search],
    };
    return (
      <div className="flex flex-wrap gap-1.5 mt-2" data-options-e7>
        {filteredOpts.map((opt, i) => { const [cls, Icone] = STYLE_E7[boutonsE7[i]]; return (
          <button key={opt} type="button" data-option-e7={boutonsE7[i]} disabled={!isActive} onClick={() => onSend(opt, undefined, undefined, { workspacePhase })}
            className={cls}><Icone className="h-3.5 w-3.5" />{opt}</button>); })}
      </div>
    );
  }

  const isQuestion = (opt: string) => /\?\s*$/.test(opt.trim());
  const hasSelection = selected.size > 0;

  const doSend = (text: string) => {
    if (msgType === "consultation" && agent && activeRoster.length > 1) {
      onSend(text, agent, undefined, { workspacePhase });
    } else {
      onSend(text, undefined, undefined, { workspacePhase });
    }
    setSelected(new Set());
    setInlineTexts({});
    setPrecisionTexts({});
    setGlobalPrecision("");
    setShowInputFor(null);
  };

  const toggleSelect = (i: number) => {
    if (!isActive) return;
    const opt = filteredOpts[i];

    // Question → toggle input inline
    if (isQuestion(opt)) {
      const next = new Set(selected);
      if (next.has(i)) {
        next.delete(i);
        setShowInputFor(null);
        const nextTexts = { ...inlineTexts };
        delete nextTexts[i];
        setInlineTexts(nextTexts);
      } else {
        next.add(i);
        setShowInputFor(i);
      }
      setSelected(next);
      return;
    }

    // Non-question → toggle sélection
    const next = new Set(selected);
    if (next.has(i)) {
      next.delete(i);
      const nextP = { ...precisionTexts };
      delete nextP[i];
      setPrecisionTexts(nextP);
    } else {
      next.add(i);
    }
    setSelected(next);
  };

  const handleSendAll = () => {
    if (selected.size === 0) return;
    const parts: string[] = [];
    for (const i of Array.from(selected).sort()) {
      if (isQuestion(filteredOpts[i]) && inlineTexts[i]?.trim()) {
        parts.push(inlineTexts[i].trim());
      } else {
        let optText = filteredOpts[i];
        if (precisionTexts[i]?.trim()) optText += ` — ${precisionTexts[i].trim()}`;
        parts.push(optText);
      }
    }
    let finalText = parts.join("\n\n");
    if (globalPrecision.trim()) finalText += `\n\nContexte: ${globalPrecision.trim()}`;
    doSend(finalText);
  };

  const handleInlineSubmit = (i: number) => {
    if (!inlineTexts[i]?.trim()) return;
    doSend(inlineTexts[i].trim());
  };

  const borderColors = ["border-l-blue-500", "border-l-amber-500", "border-l-green-500", "border-l-red-500"];
  const hoverBgs = isActive ? ["hover:bg-blue-50", "hover:bg-amber-50", "hover:bg-green-50", "hover:bg-red-50"] : [""];

  return (
    <div className={cn("mt-3 space-y-1.5", !isActive && "opacity-40 pointer-events-none")}>
      {filteredOpts.map((opt, i) => {
        const isSel = selected.has(i);
        const isQ = isQuestion(opt);
        const isInputOpen = isQ && showInputFor === i;
        return (
          <div key={i}>
            <button
              disabled={!isActive}
              onClick={() => toggleSelect(i)}
              style={isActive ? { animation: `fadeSlideUp 0.08s ease-out both` } : undefined}
              className={cn(
                "w-full text-left border rounded-lg px-3 py-2 transition-all",
                "border-l-[3px]",
                borderColors[i % borderColors.length],
                isSel ? "bg-blue-50 border-blue-300 ring-1 ring-blue-200" : "border-gray-200",
                isInputOpen ? "bg-amber-50/50 border-amber-200" : "",
                isActive && !isSel && !isInputOpen && hoverBgs[i % hoverBgs.length],
                isActive ? "hover:shadow-sm cursor-pointer group/opt" : "cursor-default",
                isActive && "active:scale-[0.98] focus:outline-none touch-manipulation",
              )}
            >
              <div className="flex items-start gap-2">
                <span className={cn(
                  "mt-0.5 shrink-0 w-4 h-4 rounded border flex items-center justify-center transition-colors",
                  isSel ? "bg-blue-500 border-blue-500 text-white" : "border-gray-300"
                )}>
                  {isSel && <Check className="h-3 w-3" />}
                </span>
                <span className={cn("text-sm font-medium flex-1", isActive ? "text-gray-700 group-hover/opt:text-gray-900" : "text-gray-400")}>
                  {opt}
                </span>
                {isQ && <MessageCircle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />}
              </div>
            </button>
            {/* Input inline pour les options-questions */}
            {isInputOpen && (
              <div ref={(el) => { if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50); }} className="mt-1 ml-6 flex gap-1.5 animate-in fade-in slide-in-from-top-1 duration-200">
                <input
                  type="text"
                  autoFocus
                  placeholder="Votre réponse..."
                  value={inlineTexts[i] || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setInlineTexts(prev => ({ ...prev, [i]: e.target.value }))}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleInlineSubmit(i); } }}
                  className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300"
                />
                <button
                  onClick={() => handleInlineSubmit(i)}
                  disabled={!inlineTexts[i]?.trim()}
                  className="px-2.5 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-medium disabled:opacity-40 hover:bg-blue-600 transition-colors cursor-pointer"
                >
                  <Send className="h-3.5 w-3.5" />
                </button>
              </div>
            )}
            {/* Precision input pour les options non-question selectionnees */}
            {isSel && !isQ && (
              <div ref={(el) => { if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50); }} className="mt-1 ml-6 animate-in fade-in slide-in-from-top-1 duration-200">
                <input
                  type="text"
                  placeholder="Preciser (optionnel)..."
                  value={precisionTexts[i] || ""}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setPrecisionTexts(prev => ({ ...prev, [i]: e.target.value }))}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendAll(); } }}
                  className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-200 bg-gray-50/50 placeholder:text-gray-300"
                />
              </div>
            )}
          </div>
        );
      })}
      {/* Global context input quand 2+ options selectionnees */}
      {selected.size > 1 && (
        <div className="mt-1 animate-in fade-in duration-200">
          <input
            type="text"
            placeholder="Contexte global pour vos choix (optionnel)..."
            value={globalPrecision}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setGlobalPrecision(e.target.value)}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendAll(); } }}
            className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-blue-200 bg-gray-50/50 placeholder:text-gray-300"
          />
        </div>
      )}
      {/* Bouton Envoyer — toujours visible dès qu'une option est cochée */}
      {hasSelection && (
        <button
          ref={(el) => { if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "nearest" }), 50); }}
          onClick={handleSendAll}
          className="w-full mt-1.5 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-blue-500 text-white text-sm font-semibold hover:bg-blue-600 transition-colors cursor-pointer shadow-sm animate-in fade-in duration-200"
        >
          <Send className="h-4 w-4" />
          Envoyer{selected.size > 1 ? ` (${selected.size} sélectionnés)` : ""}
        </button>
      )}
    </div>
  );
}

// ═══ V3 MESSAGE LIST — Système unique de rendu des discussions ═══
// Gère: bulles V3, options cliquables, streaming, thinking, coaching, voice
function V3MessageList() {
  const { messages, isTyping, sendMessage, sendMultiPerspective, thinkingSteps, parkThread, activeRoster, addBotToRoster, chatTargetBot, activeThreadId, injectVoiceMessage } = useChatContext();
  const { activeBotCode, activePhase, setActivePhase, setRightSection, setReflexionContext, reflexionContext, credoPhase, addWorkflowItem, workflowItems, chatStage, addWorkspaceBlock, removeWorkspaceBlock, workspaceBlocks, focusType, activeDocumentSection, startDeliverable, addTask, startConception } = useAmorcer();
  const discussionElementsLocal = useDiscussionElements(activeThreadId, isTyping);
  // OPTIONS.RANGEE.1 (DEC-OPTIONS-BULLE-1, règle O5) — relais V3 : messages relus du serveur sans options (navigateur neuf, autre appareil)
  // → options enregistrées avec la réponse (GET /discussions/{fil}/messages, champ « options »), par identifiant de message
  const [optionsServeur, setOptionsServeur] = useState<Record<string, string[]>>({});
  const sansOptions = messages.filter(m => m.role === "assistant" && !m.isStreaming && !m.options?.length).length;
  useEffect(() => {
    if (!activeThreadId || sansOptions === 0) return;
    let annule = false;
    api.getDiscussionMessages(activeThreadId).then((r) => {
      if (annule) return;
      const parId: Record<string, string[]> = {};
      for (const m of (r.messages || []) as Array<{ id: string; options?: Array<{ label: string }> }>) {
        if (m.options?.length) parId[String(m.id)] = m.options.map(o => o.label);
      }
      setOptionsServeur(parId);
    }).catch(() => { /* relecture impossible : pas d'options relues, comme avant */ });
    return () => { annule = true; };
  }, [activeThreadId, sansOptions]);
  // S229 lot 7e (P6) : à l'ouverture d'un fil, les gestes que portent les bulles du code (extra.options[].geste, relu par /messages) ; une
  // option de ces bulles garde sa route sans attendre /elements. versionBulles redessine les options une fois les gestes lus.
  const [versionBulles, setVersionBulles] = useState(0);
  useEffect(() => {
    if (!activeThreadId) return;
    let annule = false;
    api.getDiscussionMessages(activeThreadId).then((r) => {
      if (annule) return;
      for (const m of (r.messages || []) as Array<{ options?: unknown[] }>) enregistrerChoixBulles(activeThreadId, m.options);
      setVersionBulles((v) => v + 1);
    }).catch(() => { /* relecture impossible : /elements reste la source des gestes, comme avant */ });
    return () => { annule = true; };
  }, [activeThreadId]);
  const ecranGreffierActif = !!(discussionElementsLocal?.ecran_greffier && discussionElementsLocal?.a_elements);
  // S229 lot 7d (section 3.1) : la bulle de fin de plan, écrite par le code après la réponse de la voix, rendue par /elements — montrée une
  // fois ; pas en double quand l'historique relu du serveur la porte déjà
  const bullesCodeMontrees = useRef<Set<number>>(new Set());
  const bulleCode = discussionElementsLocal?.bulle_code;
  useEffect(() => {
    if (!bulleCode || bullesCodeMontrees.current.has(bulleCode.id)) return;
    bullesCodeMontrees.current.add(bulleCode.id);
    enregistrerChoixBulles(activeThreadId, bulleCode.gestes);  // S229 lot 7e (P6)
    setVersionBulles((v) => v + 1);
    if (messages.some(m => m.role === "assistant" && m.content === bulleCode.texte)) return;
    injectVoiceMessage("assistant", bulleCode.texte, "CEOB", { options: bulleCode.options });
  }, [bulleCode?.id]);  // eslint-disable-line react-hooks/exhaustive-deps
  // Enrichir activePhase avec le step CREDO pour que le backend injecte le bon prompt
  // URL-based fallback: activePhase peut être "reflexion" si l'état a dérivé
  const _credoSteps = ["comprendre", "rechercher", "exposer", "demontrer", "objectif"];
  const _isDiscussionURL = window.location.pathname.includes("/discussion/");
  const _effectiveDiscussion = activePhase === "discussion" || (_isDiscussionURL && !activePhase.startsWith("discussion"));
  // CHAT.WORKSPACE.CHAINE.1 lot 5 (K4) : l'étape du greffier quand /elements la donne (la même que Carlos reçoit), sinon chatStage
  const workspacePhase = phaseEnvoyee(discussionElementsLocal?.etape_suggeree, chatStage, activePhase, _effectiveDiscussion);
  void _credoSteps;

  // W.1 Expert pipeline — ajoute un bot dans l'équipe (roster discussion) + consultation workspace
  const addExpertToWorkspace = useCallback(async (code: string) => {
    addBotToRoster(code);
    // Fix S131: utiliser le step du dernier bloc workspace (chatStage peut être décalé par phase_credo backend)
    const credoStep = (workspaceBlocks.length > 0
      ? workspaceBlocks[workspaceBlocks.length - 1].credo_step
      : "C") as "C" | "R" | "E" | "D" | "O";
    const tempId = `catching-up-${code}-${Date.now()}`;
    addWorkspaceBlock({
      id: tempId,
      type: "catching_up",
      title: `${BOT_NAME[code] || code} prend connaissance...`,
      summary: "Analyse de la discussion en cours...",
      structured_data: {},
      credo_step: credoStep,
      credo_sub_section: "experts",
      confidence: 0.5,
      source: code,
      sourceType: "chat",
      timestamp: Date.now(),
      is_catching_up: true,
    } as any);
    try {
      const lastUserMsg = messages.filter(m => m.role === "user").pop()?.content || "";
      const lastBotMsg = messages.filter(m => m.role === "assistant" && m.content).pop()?.content || "";
      const recentCtx = workspaceBlocks.slice(-3)
        .map(b => `[${BOT_NAME[b.source || ""] || ""}] ${b.title}: ${(b.summary || "").substring(0, 150)}`)
        .join("\n");
      const context = lastUserMsg || lastBotMsg
        ? `Question: ${lastUserMsg}\n\nAnalyse en cours: ${lastBotMsg.substring(0, 500)}\n\nContexte récent:\n${recentCtx}`
        : `Présente ce que tu apportes à cette discussion (3-4 phrases). Sois direct et propose 2-3 pistes concrètes.`;
      const res = await api.chatMulti({
        message: context,
        user_id: 1,
        agents: [code],
        primary_agent: code,
        workspace_phase: workspacePhase,
      });
      const persp = res.perspectives?.[0];
      if (persp) {
        // Strip internal scaffold markers que le LLM peut échoer
        const _INTERNAL_MARKER_RE = /^\[(EQUIPE PRESENTE|BOT PRINCIPAL|CONSULTANT\s*[—\-])[^\]]*\]\s*/gim;
        const cleanContent = (persp.contenu || "")
          .replace(_INTERNAL_MARKER_RE, "")
          .replace(/^---+\s*/gm, "")
          .trim();
        if (cleanContent.length > 10) {
          // C.49 — Chemin normalisé: detectBlockTypeFrontend + extractStructuredDataFrontend
          crystallizeAndAddBlock(cleanContent, {
            addWorkspaceBlock,
            botCode: code,
            credoStep,
            credo_sub_section: "experts",
            confidence: 0.75,
            forceTitle: generateExpertBlockTitle(BOT_NAME[code] || code, cleanContent, "Apport expert"),
            replace_block_id: tempId,
            blockId: `expert-${code}-${Date.now()}`,
            backendBlock: persp.workspace_block as import("./core/types").WorkspaceBlock | undefined,
          });
        } else {
          removeWorkspaceBlock(tempId);
        }
      } else {
        removeWorkspaceBlock(tempId);
      }
    } catch (err) {
      console.error("[ExpertAdd]", err);
      removeWorkspaceBlock(tempId);
    }
  }, [addBotToRoster, messages, workspaceBlocks, addWorkspaceBlock, removeWorkspaceBlock, workspacePhase, chatStage]);

  const endRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const userScrolledUp = useRef(false);
  const isAnyStreaming = messages.some(m => m.isStreaming);
  // Si le DERNIER message est un assistant non-streaming avec contenu, le bot a fini de repondre
  // → supprimer TOUT indicateur de reflexion (thinking steps + typing dots)
  const lastMsg = messages.length > 0 ? messages[messages.length - 1] : null;
  const botAlreadyResponded = lastMsg?.role === "assistant" && !lastMsg.isStreaming && (lastMsg.content?.length ?? 0) > 0;

  // Dernier message bot AVEC OPTIONS (pour activer les options du bon message)
  // Ne pas se fier au dernier message bot en général — les messages sans options
  // (ex: auto-consultation C.32) ne doivent pas désactiver les options du message principal.
  const lastBotId = [...messages].reverse().find(m => m.role === "assistant" && !m.isStreaming && (m.options?.length ?? 0) > 0)?.id
    ?? [...messages].reverse().find(m => m.role === "assistant" && !m.isStreaming)?.id;
  // Dernier message user (pour cristallisation intelligente)
  const lastUserMessage = [...messages].reverse().find(m => m.role === "user")?.content || "";

  // Écouter bt-execute-task — bouton "Démarrer" depuis les blocs workspace
  useEffect(() => {
    const handler = (e: Event) => {
      const { prompt, bot } = (e as CustomEvent).detail || {};
      if (!prompt) return;
      const targetBot = bot || chatTargetBot;
      // L'user a cliqué un bouton action — il veut voir la réponse → force-scroll vers le bas
      userScrolledUp.current = false;
      endRef.current?.scrollIntoView({ behavior: "smooth" });
      // C.50 — Passer les blocs existants pour que le backend puisse ENRICH/MERGE
      const _wsCtx = workspaceBlocks.slice(-10).map(b => ({ id: b.id, title: (b.title || "").slice(0, 60), type: b.type, summary: (b.summary || "").slice(0, 200) }));
      sendMessage(prompt, targetBot, undefined, { workspacePhase, workspaceBlocksContext: _wsCtx.length > 0 ? _wsCtx : undefined });
    };
    window.addEventListener("bt-execute-task", handler);
    return () => window.removeEventListener("bt-execute-task", handler);
  }, [sendMessage, chatTargetBot, workspacePhase, workspaceBlocks]);

  // Détecter si l'user a scrollé vers le haut (désactive l'auto-scroll)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => {
      const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      userScrolledUp.current = distFromBottom > 120;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // Auto-scroll intelligent — seulement si l'user est en bas
  useEffect(() => {
    if (!userScrolledUp.current) {
      endRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);
  // Streaming scroll — instant (pas smooth) pour éviter le race condition où
  // l'animation smooth n'a pas fini → distFromBottom > 120 → userScrolledUp=true → scroll perdu
  useEffect(() => {
    if (!isAnyStreaming) return;
    const id = setInterval(() => {
      if (!userScrolledUp.current && scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    }, 80);
    return () => clearInterval(id);
  }, [isAnyStreaming]);
  // Quand le streaming se termine, les options apparaissent → scroll pour les montrer
  const prevStreamingRef = useRef(false);
  useEffect(() => {
    if (prevStreamingRef.current && !isAnyStreaming) {
      setTimeout(() => {
        if (!userScrolledUp.current) {
          endRef.current?.scrollIntoView({ behavior: "smooth" });
        }
      }, 150);
    }
    prevStreamingRef.current = isAnyStreaming;
  }, [isAnyStreaming]);

  // Option click — gère les cas spéciaux coaching + envoi standard
  // P-S232-36 (G2, F3) : l'option cliquée pendant que Carlos écrit est gardée et jouée à la fin de sa réponse (avant : le message le
  // promettait, rien n'attendait)
  const optionEnAttenteRef = useRef<string | null>(null);
  const handleOption = useCallback(async (opt: string) => {
    // P-S232-31 n° 11 : un clic pendant que Carlos écrit ne part pas tout de suite, et le dit
    if (isTyping) { optionEnAttenteRef.current = opt; signalerEcran("Carlos répond encore — votre choix attendra la fin de sa réponse."); return; }
    // S229 lot H (DEC-CONFIRMER-OPTION-1, OPTIONS.GESTE.CHAT.1) : une option qui porte un choix du workspace (geste en attente du serveur :
    // sujet, précision, piste, décision, décideur) le confirme par la même route que le bouton, puis part comme message ; un texte tapé
    // librement ne passe pas ici et reste « proposée »
    const choixGeste = activeThreadId ? choixDuChat(discussionElementsLocal, opt, activeThreadId) : null;  // P6 : la bulle d'abord
    // S229 lot 7d (trou 1) : un choix « sans_message » n'envoie pas l'option — sa route écrit la bulle suivante (« Créer le chantier »,
    // « Ajuster d'abord »), ou c'est une action de l'écran (« Voir le chantier », « Passer en Conception », « Rester dans la discussion »)
    const quoi = actionDuChoix(choixGeste);
    if (choixGeste && activeThreadId && quoi !== "message") {
      if (quoi === "route") await executerChoixGeste(activeThreadId, choixGeste, opt);  // P-S232-31 n° 6 : le geste est attendu
      else if (quoi === "voir_chantier") { demanderChantierAOuvrir(choixGeste.element_id); setRightSection("chantiers"); }
      else if (quoi === "conception") startConception();
      return;
    }
    // P-S232-31 n° 6 (REVUE-NUIT ; une piste retenue en double) : le geste est attendu avant que le message parte ; le message part ensuite
    // dans tous les cas (un geste rendu faux, p. ex. le nom du décideur demandé, ne bloque pas la discussion)
    if (choixGeste && activeThreadId) await executerChoixGeste(activeThreadId, choixGeste, opt);
    const lower = opt.toLowerCase();
    if (lower.includes("parker") && lower.includes("thread")) { parkThread(); return; }
    if (lower.includes("synthes") || lower.includes("synthét") || lower.includes("faire le point")) {
      sendMessage("Fais une synthèse structurée de notre discussion.", chatTargetBot);
      return;
    }

    // Detection livrable DocForge — "Ouvrir l'atelier X"
    // JUMELAGE EXCLU: doit etre demande explicitement par l'utilisateur via navigation sidebar
    // (protocole de questions + contexte ressource requis avant jumelage)
    const atelierMatch = opt.match(/(?:ouvrir|activer|lancer)\s+l'atelier\s+(document|tableur|presentation|code)/i);
    if (atelierMatch) {
      const deliverableType = atelierMatch[1].toLowerCase();
      startDeliverable(deliverableType);
      return;
    }
    // Detection "Cahier de projet / charges" — bouton explicite du bot (ex: Paco "Lancer le Cahier")
    const cahierMatch = opt.match(/(?:lancer|ouvrir|créer|démarrer|commencer|activer)\s+le\s+cahier/i)
      || opt.match(/cahier\s+de\s+(?:projet|charges)/i)
      || opt.match(/(?:lancer|ouvrir)\s+docforge/i);
    if (cahierMatch) {
      startDeliverable("cprj");
      return;
    }

    // Retour au cockpit (fin de rétroaction)
    if (lower.includes("retour au cockpit")) {
      setActivePhase("observation" as PhaseKey);
      setReflexionContext(null);
      setRightSection("cockpit");
      return;
    }

    // Transition de phase workspace (option cliquable "Passer en mode X")
    // Les boutons contextuels (contextual-actions.ts) contrôlent déjà QUELS boutons sont visibles
    const phaseMatch = lower.match(/passer en mode (discussion|réflexion|conception|exécution|rétroaction)/);
    if (phaseMatch) {
      const labelToPhase: Record<string, string> = {
        "discussion": "discussion",
        "réflexion": "reflexion",
        "conception": "creation",
        "exécution": "execution",
        "rétroaction": "retroaction",
      };
      const targetPhase = labelToPhase[phaseMatch[1]];
      if (targetPhase) {
        const context = reflexionContext || "discussion en cours";
        // Flux inter-phases: injecter les notes épinglées de la phase précédente
        const prevPhaseNotes = workflowItems.filter(w => w.phase === activePhase);
        const notesContext = prevPhaseNotes.length > 0
          ? `\n\nNotes de la phase ${activePhase}:\n${prevPhaseNotes.map(n => `• ${n.text}`).join("\n")}`
          : "";
        setActivePhase(targetPhase as PhaseKey);
        setReflexionContext(context);
        if (targetPhase === "execution" || targetPhase === "retroaction") {
          setRightSection(null);
        } else {
          setRightSection(null);
        }
        const prompts: Record<string, string> = {
          discussion: "Parlons de",
          reflexion: "Analyse approfondie :",
          creation: "Conception pour",
          execution: "Plan d'exécution pour",
          retroaction: "Bilan et rétroaction sur",
        };
        setTimeout(() => sendMessage(`${prompts[targetPhase]} ${context}${notesContext}`, chatTargetBot, undefined, { workspacePhase: targetPhase }), 80);
        return;
      }
    }

    // Techniques de réflexion — envoyer le VRAI prompt, pas juste le label
    const TECHNIQUE_PROMPTS: Record<string, string> = {
      "brainstorm": "Lance un brainstorm créatif tous azimuts sur: ",
      "scamper": "Applique SCAMPER (Substituer, Combiner, Adapter, Modifier, Put to other use, Éliminer, Réorganiser) à: ",
      "5 pourquoi": "Analyse les 5 Pourquoi en profondeur pour: ",
      "6 chapeaux": "Analyse avec les 6 Chapeaux de Bono (Blanc=faits, Rouge=émotions, Noir=risques, Jaune=bénéfices, Vert=créativité, Bleu=processus) pour: ",
      "challenger": "Joue l'avocat du diable, challenge cette approche et trouve les failles de: ",
      "deep search": "Recherche approfondie — tendances, benchmarks, meilleures pratiques pour: ",
      "analyser": "Analyse approfondie structurée de: ",
    };
    const techniquePrompt = TECHNIQUE_PROMPTS[lower];
    if (techniquePrompt) {
      const topic = reflexionContext || messages.filter(m => m.role === "user").pop()?.content?.substring(0, 100) || "discussion en cours";
      // Transition vers Reflexion SEULEMENT si on n'est pas en Discussion
      // En Discussion, on applique la technique sans changer de phase (Bible Live 4.18)
      // La transition explicite se fait via le bouton "Passer en mode reflexion"
      if (activePhase !== "reflexion" && activePhase !== "discussion") {
        setActivePhase("reflexion" as PhaseKey);
        setReflexionContext(topic);
        setRightSection(null);
      }
      sendMessage(techniquePrompt + topic, chatTargetBot, undefined, { workspacePhase });
      return;
    }

    // "Cristalliser vers [Section]" — Sprint 6
    if (lower.startsWith("cristalliser vers ")) {
      const lastBotMsg = messages.filter(m => m.role === "assistant").pop();
      if (lastBotMsg && activeDocumentSection) {
        const lastUserMsg = messages.filter(m => m.role === "user").pop()?.content;
        cristalliseViaAPI(lastBotMsg.content, {
          botCode: activeBotCode,
          sectionId: activeDocumentSection,
          credoStep: "C",
          userMsg: lastUserMsg,
          addWorkspaceBlock,
        });
      }
      return;
    }

    // DEC-ETAPES-MENU-1 : un clic d'option ne crée plus de bloc « Décision: » (C.49) ; le greffier reste la source des décisions et le
    // clic reste un message du fil (O6)

    // W.1: Discussion 1:1 — toujours single-bot (experts dans le workspace)
    sendMessage(opt, chatTargetBot, undefined, { workspacePhase, workspaceExpertContext: buildExpertContext(workspaceBlocks, activeBotCode) });
  }, [isTyping, sendMessage, sendMultiPerspective, chatTargetBot, activeBotCode, activeRoster, parkThread, setActivePhase, setRightSection, setReflexionContext, reflexionContext, activePhase, workspacePhase, messages, activeDocumentSection, addWorkspaceBlock, workflowItems, chatStage, activeThreadId, discussionElementsLocal, startConception]);

  // P-S232-36 F3 : la fin de la réponse joue l'option gardée (une seule, la dernière cliquée)
  useEffect(() => {
    if (isTyping || !optionEnAttenteRef.current) return;
    const o = optionEnAttenteRef.current; optionEnAttenteRef.current = null;
    void handleOption(o);
  }, [isTyping, handleOption]);

  return (
    <div ref={scrollRef} className="flex-1 overflow-auto px-4 py-3 space-y-3 scrollbar-discussion">
      {messages.map((msg) => {
        if (msg.role === "system") return null;
        // C.BUFFER: masquer la bulle pendant le streaming actif SEULEMENT
        // (isTyping=false sur refresh → pas de masquage des messages stale)
        // P-S232-31 E5j (REVUE-NUIT n° 17) : le texte qui arrive s'affiche pendant le flux ; la bulle reste cachée tant qu'elle est vide
        if (msg.isStreaming && isTyping && !(msg.content || "").trim()) return null;
        if ((msg.msgType as string) === "team_proposal") return null; // W.1: team_proposal consumed by workspace, not shown in chat

        // ── Multi-thinking bubble — animation consultation multi-agent ──
        // Variante "join" (msg.content = bot code) : un bot rejoint la discussion
        // Variante "consult" (msg.content vide) : consultation de tous les bots
        if ((msg.msgType as string) === "multi-thinking") {
          const joinBotCode = msg.content && msg.content.length <= 10 && msg.content.match(/^[A-Z]+$/) ? msg.content : null;
          const userText = (msg as any).userText as string | undefined;
          const headerText = joinBotCode
            ? `${BOT_NAME[joinBotCode] || joinBotCode} rejoint la discussion...`
            : "Consultation des départements...";
          return (
            <div key={msg.id} className="flex gap-2.5 animate-in fade-in slide-in-from-bottom-2 duration-500">
              <div className="ml-2 flex-1">
                <div className="bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 border border-blue-200/60 rounded-2xl px-5 py-4 shadow-md">
                  <div className="flex items-center gap-2.5 mb-3">
                    <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                      <Network className="h-3.5 w-3.5 text-blue-600 animate-pulse" />
                    </div>
                    <span className="text-xs font-bold text-blue-900 tracking-wide">{headerText}</span>
                  </div>
                  <div className="flex items-start justify-center gap-6">
                    {activeRoster.map((code) => {
                      const ts = V3_STYLE[code] || DEFAULT_STYLE;
                      const isNewBot = joinBotCode === code;
                      const isExisting = !!joinBotCode && joinBotCode !== code;
                      return (
                        <div key={code} className={cn(
                          "flex flex-col items-center gap-1.5 transition-all duration-500",
                          isNewBot && "scale-110",
                        )}>
                          <div className={cn(
                            "relative w-12 h-12 rounded-full overflow-hidden border-2 shadow-md transition-all",
                            isNewBot ? cn(ts.ring, "shadow-lg ring-2 ring-offset-1", ts.ring) : isExisting ? "border-green-400 shadow-green-100" : cn("border-2", ts.ring),
                          )}>
                            <img src={BOT_AVATAR[code] || `/agents/${code.toLowerCase()}.png`} alt=""
                              className={cn("w-full h-full object-cover", (isNewBot || !joinBotCode) && "opacity-60")} />
                            {(isNewBot || !joinBotCode) && (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                                <Loader2 className={cn("h-5 w-5 animate-spin", ts.text)} />
                              </div>
                            )}
                            {isExisting && (
                              <div className="absolute inset-0 flex items-center justify-center bg-green-500/10">
                                <CheckCircle2 className="h-5 w-5 text-green-500 drop-shadow-sm" />
                              </div>
                            )}
                          </div>
                          <span className={cn("text-[11px] font-semibold", isNewBot ? ts.text : isExisting ? "text-gray-500" : ts.text)}>
                            {BOT_NAME[code]}
                          </span>
                          <span className={cn("text-[10px]", isExisting ? "text-gray-400" : ts.text)}>
                            {BOT_ROLE[code] || ""}
                          </span>
                          {isExisting
                            ? <span className="text-[9px] text-green-600 font-medium">✓ Terminé</span>
                            : <ThinkingLabel botCode={code} userText={userText} />
                          }
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // ── Typing bubble — animation dots pour un bot spécifique ──
        if ((msg.msgType as string) === "typing") {
          const tCode = msg.agent || activeBotCode;
          const ts = V3_STYLE[tCode] || DEFAULT_STYLE;
          return (
            <div key={msg.id} className="flex gap-2.5 animate-in fade-in duration-300">
              <div className={cn("w-7 h-7 rounded-full overflow-hidden shrink-0 ring-2 mt-0.5", ts.ring)}>
                <img src={BOT_AVATAR[tCode] || `/agents/${tCode.toLowerCase()}.png`} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="border border-gray-200 rounded-xl rounded-tl-none px-3.5 py-3 shadow-sm bg-white">
                <div className="flex items-center gap-2">
                  <span className={cn("text-[11px] font-semibold", ts.text)}>{BOT_NAME[tCode]}</span>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            </div>
          );
        }

        // ── Bot-join bubble — animation quand un bot rejoint la discussion ──
        if ((msg.msgType as string) === "bot-join") {
          const jCode = msg.agent || "CEOB";
          const js = V3_STYLE[jCode] || DEFAULT_STYLE;
          return (
            <div key={msg.id} className="flex justify-center animate-in fade-in slide-in-from-bottom-2 duration-500">
              <div className={cn("flex items-center gap-2 px-4 py-2 rounded-full border shadow-sm", js.bubble, js.border)}>
                <div className={cn("w-6 h-6 rounded-full overflow-hidden ring-2 shrink-0", js.ring)}>
                  <img src={BOT_AVATAR[jCode] || `/agents/${jCode.toLowerCase()}.png`} alt="" className="w-full h-full object-cover" />
                </div>
                <span className={cn("text-xs font-semibold", js.text)}>{BOT_NAME[jCode]}</span>
                <span className="text-xs text-gray-500">a rejoint la discussion</span>
              </div>
            </div>
          );
        }

        // ── S102-B — Bulle consolidee multi-agent ──
        if ((msg.msgType as string) === "multi-enriched") {
          const mCode = msg.agent || activeBotCode || "CEOB";
          const ms = V3_STYLE[mCode] || DEFAULT_STYLE;
          const secondaries = ((msg as any).secondaryInputs || []) as Array<{agent: string; nom: string; contenu: string}>;
          const modeActif = (msg as any).modeActif as string | undefined;
          const modeSteps = ((msg as any).modeSteps || []) as Array<{id: string; label: string}>;
          return (
            <div key={msg.id} className="flex gap-2.5 animate-in fade-in slide-in-from-bottom-2 duration-500">
              <div className={cn("w-7 h-7 rounded-full overflow-hidden shrink-0 ring-2 mt-0.5", ms.ring)}>
                <img src={BOT_AVATAR[mCode] || `/agents/${mCode.toLowerCase()}.png`} alt="" className="w-full h-full object-cover" />
              </div>
              <div className={cn("flex-1 border-l-[3px] border rounded-xl rounded-tl-none px-3.5 py-2.5 shadow-sm max-w-[85%]", ms.border, ms.bubble)}>
                {/* Header primaire */}
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={cn("text-[11px] font-semibold", ms.text)}>{BOT_NAME[mCode]}</span>
                  <span className="text-[9px] text-gray-400">{BOT_ROLE[mCode]}</span>
                  {msg.branchLabel && (
                    <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 font-medium">{msg.branchLabel}</span>
                  )}
                </div>
                {/* Contenu principal (markdown) */}
                <div className="text-sm text-gray-800 leading-relaxed prose-sm"
                  dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }} />
                {/* Contributions secondaires — ContributionCards style ConferenceAI */}
                {secondaries.length > 0 && (
                  <div className="mt-3 pt-2.5 border-t border-gray-200/60 space-y-2">
                    {secondaries.map((sec) => {
                      const secStyle = V3_STYLE[sec.agent] || DEFAULT_STYLE;
                      return (
                        <ContributionCard
                          key={sec.agent}
                          agent={sec.agent}
                          nom={sec.nom}
                          contenu={sec.contenu}
                          style={secStyle}
                        />
                      );
                    })}
                  </div>
                )}
                {/* S102-B Phase 7 — Mode steps indicator */}
                {modeSteps.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-2">
                    <span className="text-[9px] text-gray-400 uppercase tracking-wider mr-1">{modeActif}</span>
                    {modeSteps.map((step, i) => (
                      <div key={i} className={cn(
                        "px-2 py-0.5 rounded-full text-[9px] font-medium border",
                        i === 0
                          ? "bg-blue-50 border-blue-200 text-blue-700"
                          : "bg-gray-50 border-gray-200 text-gray-400"
                      )}>
                        {step.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        }

        const isUser = msg.role === "user";
        const botCode = msg.agent || activeBotCode || "CEOB";
        const s = V3_STYLE[botCode] || DEFAULT_STYLE;
        const isCoaching = msg.msgType === "coaching";
        const isLast = msg.id === lastBotId;

        // ── User bubble ──
        if (isUser) {
          // S3B.2: Detect workspace-sourced actions (pill indicator)
          const wsMatch = (msg.content || "").match(/^(Approfondir en detail|Challenge cet element, trouve les failles|Retravaille et enrichis):\s*(.+?)(?:\n|$)/);
          const wsLabel = wsMatch ? (wsMatch[1].startsWith("Approfondir") ? "Approfondir" : wsMatch[1].startsWith("Challenge") ? "Challenger" : "Modifier") : null;
          const wsTitle = wsMatch?.[2]?.substring(0, 60) || null;
          return (
            <div key={msg.id} className="flex gap-2.5 justify-end">
              <div className="max-w-[80%]">
                {wsLabel && wsTitle && (
                  <div className="flex justify-end mb-1">
                    <span className="text-[10px] bg-sky-50 border border-sky-200 rounded-full px-2 py-0.5 text-sky-600 font-medium">
                      [{wsLabel}] {wsTitle}
                    </span>
                  </div>
                )}
                <div className="bg-blue-50 border border-blue-100 rounded-xl rounded-tr-none px-3.5 py-2.5 shadow-sm">
                  <p className="text-sm text-blue-900 whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
              <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 mt-0.5 ring-2 ring-blue-300">
                <img src="/agents/carl-fugere.jpg" alt="Carl" className="w-full h-full object-cover" />
              </div>
            </div>
          );
        }

        // ── Coaching bubble (CarlOS sentinelle — style ambre) ──
        if (isCoaching) {
          return (
            <div key={msg.id} className="flex gap-2.5">
              <div className="w-7 h-7 rounded-full overflow-hidden shrink-0 ring-2 ring-amber-300 mt-0.5">
                <img src={BOT_AVATAR.CEOB} alt="CarlOS" className="w-full h-full object-cover" />
              </div>
              <div className="flex-1 max-w-[85%]">
                <div className="border-l-[3px] border-amber-400 border border-amber-200 rounded-xl rounded-tl-none px-3.5 py-2.5 shadow-sm bg-amber-50/40">
                  <div className="flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="h-3 w-3 text-amber-600" />
                    <span className="text-[11px] font-semibold text-amber-700">CarlOS — Sentinelle</span>
                  </div>
                  <p className="text-sm text-amber-800">{msg.content}</p>
                </div>
              </div>
            </div>
          );
        }

        // ── Bot bubble standard ──
        return (
          <div key={msg.id} className="flex gap-3 group/msg">
            <div className={cn("w-8 h-8 rounded-full overflow-hidden shrink-0 ring-2 mt-1 shadow-sm", s.ring)}>
              <img src={BOT_AVATAR[botCode] || `/agents/${botCode.toLowerCase()}.png`}
                alt={BOT_NAME[botCode] || botCode} className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 max-w-[85%] relative">
              <div className={cn(
                "border-l-[3px] rounded-2xl rounded-tl-none px-4 py-3 shadow-sm",
                "bg-gradient-to-br from-white via-white to-gray-50/80",
                "border border-gray-100",
                s.border, s.bubble
              )}>
                {/* Agent name + role badge */}
                <div className="flex items-center gap-2 mb-1.5">
                  <span className={cn("text-[12px] font-bold tracking-tight", s.text)}>{BOT_NAME[botCode] || botCode}</span>
                  <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase tracking-wide">{BOT_ROLE[botCode] || ""}</span>
                </div>
                {/* Content — formatMarkdown TOUJOURS (key stable = pas d'unmount/remount) */}
                <div
                  key={msg.id}
                  className="text-[13px] text-gray-700 leading-[1.7] isolate overflow-hidden [&>p]:my-1 [&>ul]:my-1.5 [&>ol]:my-1.5 [&>p:first-child]:mt-0 [&>p:last-child]:mb-0 [&>hr]:my-3 [&_li]:break-words [&_p]:break-words [&>ul]:pl-4 [&>ol]:pl-4 [&_strong]:text-gray-900 [&_strong]:font-semibold"
                  dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }}
                />
                {msg.isStreaming && (
                  <span className="inline-block w-0.5 h-4 bg-current ml-0.5 animate-pulse align-text-bottom" />
                )}
                {/* ═══ Niveau 1 — Options DANS la bulle (multi-select + réponse inline questions) ═══ */}
                {/* DEC-OPTIONS-CONFIRMER-1 (S225) : une seule rangée sous la bulle = les options de Carlos, 3 au plus (DEC-OPTIONS-BULLE-1, O1) ;
                    les gestes du greffier (Confirmer, Décider…) restent dans les boîtes du workspace ; options relues du serveur au besoin (O5) */}
                {/* DEC-OPTIONS-SCENARIO-1 (S227) : une bulle qui pose une question du scénario a 4 options — ses trois réponses types et
                    « Je ne sais pas. », toujours en dernier (texte du skin, envoyé par le serveur) ; les autres bulles gardent 3 options au plus */}
                {!msg.isStreaming && (() => { const toutes = msg.options?.length ? msg.options : optionsServeur[String(msg.id)]; const opts = toutes?.slice(0, toutes?.some((o) => RE_OPTION_DEROULEMENT.test(String(o || "").trim())) ? 5 : toutes?.[3] === OPTION_JE_NE_SAIS_PAS || String(toutes?.[3] || "").startsWith("Confirmer : ") ? 4 : 3);  /* S230 lot H (QC-Q8 a) : « Confirmer » en quatrième */ return opts && opts.length > 0 && (<>
                  <InlineOptions
                    key="options"
                    options={opts}
                    onSend={(text, targetBot, _meta, extra) => {
                      if (targetBot) {
                        sendMessage(text, targetBot, undefined, extra);
                      } else {
                        handleOption(text);
                      }
                    }}
                    isActive={isLast || (!!msg.agent && msg.agent !== activeBotCode)
                      || bulleAuGesteEnAttente(discussionElementsLocal, opts, activeThreadId)}  // S230 lot A (C1) : bulle au geste en attente
                    msgType={msg.msgType}
                    agent={msg.agent}
                    activeRoster={activeRoster}
                    workspacePhase={workspacePhase}
                    fil={activeThreadId}
                    versionBulles={versionBulles}
                  /></>
                ); })()}
                {/* ExpertSuggestionChips retiré — les consultation suggestions sont gérées dans la zone workspace
                   via latestConsultationSuggestions → handleConsultBot → workspace block (pas bulle discussion) */}
                {/* ═══ Cascade suggestion chips — reflexion/workspace actions ═══ */}
                {!msg.isStreaming && isLast && msg.cascadeSuggestions && msg.cascadeSuggestions.length > 0 && (
                  <CascadeChips
                    suggestions={msg.cascadeSuggestions}
                    onAction={(s) => sendMessage(s.message, chatTargetBot)}
                  />
                )}
                {/* ═══ Team suggestion — C.3 Auto-Monte: phrase punch + bouton ajouter bot ═══ */}
                {!msg.isStreaming && isLast && (msg as any).teamSuggestion && (
                  <TeamSuggestionChip
                    suggestion={(msg as any).teamSuggestion as TeamSuggestionEvent}
                    onAdd={(code) => addExpertToWorkspace(code)}
                  />
                )}
                {/* ═══ Auto-consultation pills — D2 fix: consultations bot en pills expandables ═══ */}
                {!msg.isStreaming && (msg as any).autoConsultations && (msg as any).autoConsultations.length > 0 && (
                  <AutoConsultationPills
                    consultations={(msg as any).autoConsultations as AutoConsultationData[]}
                    onAddToWorkspace={(c, selectedOption) => {
                      const _title = selectedOption
                        ? `${BOT_NAME[c.bot_code] || c.bot_nom} — ${selectedOption}`
                        : `Consultation ${BOT_NAME[c.bot_code] || c.bot_nom}`;
                      const _summary = selectedOption
                        ? `Focus retenu : ${selectedOption}\n\n${(c.contenu || "").trim().slice(0, 1800)}`
                        : (c.contenu || "").trim().slice(0, 2000);
                      const _consultStep = (workspaceBlocks.length > 0
                        ? workspaceBlocks[workspaceBlocks.length - 1].credo_step
                        : "C") as "C" | "R" | "E" | "D" | "O";
                      // C.49 — Chemin normalisé: detectBlockTypeFrontend sur le contenu de consultation
                      crystallizeAndAddBlock(_summary, {
                        addWorkspaceBlock,
                        botCode: c.bot_code,
                        credoStep: _consultStep,
                        forceTitle: _title,
                        confidence: 0.85,
                        blockId: `expert-consult-${c.bot_code}-${Date.now()}`,
                      });
                    }}
                  />
                )}
                {/* ═══ Confier à la file — C.BUBBLE.EXEC.1 (S162) ═══ */}
                {!msg.isStreaming && isLast && (msg.content?.trim().length ?? 0) > 80 && (
                  <ConfierBubbleButton
                    botCode={botCode}
                    messageContent={msg.content}
                    threadId={activeThreadId || ""}
                    onAssign={addTask}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Thinking step — UNE ligne qui défile, contextuelle par bot + icones S102-B */}
      {isTyping && !botAlreadyResponded && (thinkingSteps.length > 0 || isAnyStreaming)
        && !messages.some(m => m.isStreaming && (m.content || "").trim()) && (() => {  // P-S232-31 E5j : l'indicateur cède la place au texte qui arrive
        const streamMsg = [...messages].reverse().find(m => m.role === "assistant" && m.isStreaming);
        const thinkBot = streamMsg?.agent || activeBotCode;
        const ts = V3_STYLE[thinkBot] || DEFAULT_STYLE;
        const botName = BOT_NAME[thinkBot] || "CarlOS";
        const currentStep = thinkingSteps[thinkingSteps.length - 1] || "En traitement...";
        return (
          <div className="flex gap-2.5 animate-in fade-in duration-300">
            <div className={cn("w-7 h-7 rounded-full overflow-hidden shrink-0 ring-2 mt-0.5", ts.ring)}>
              <img src={BOT_AVATAR[thinkBot] || `/agents/${thinkBot.toLowerCase()}.png`} alt="" className="w-full h-full object-cover" />
            </div>
            <div className={cn("border-l-[3px] border border-gray-200 rounded-xl rounded-tl-none px-3.5 py-2.5 shadow-sm", ts.border, ts.bubble)}>
              <div className={cn("flex items-center gap-2 text-sm", ts.text)}>
                {(() => { const DIcon = DEPT_DASH_ICON[thinkBot] || Bot; return <DIcon className="h-4 w-4 shrink-0 animate-pulse" />; })()}
                <span className="font-medium">{botName}</span>
                <EtapeReflexion etape={currentStep} />{/* D8 : textes de raisonnement qui défilent pendant l'attente */}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Typing dots — quand le bot réfléchit sans thinking steps (seulement avant la reponse bot) */}
      {isTyping && !botAlreadyResponded && !isAnyStreaming && thinkingSteps.length === 0 && messages.length > 0 && messages[messages.length - 1]?.role === "user" && (() => {
        const thinkBot2 = activeBotCode;
        const ts = V3_STYLE[thinkBot2] || DEFAULT_STYLE;
        const botName2 = BOT_NAME[thinkBot2] || "CarlOS";
        const lastUserMsg = messages.filter(m => m.role === "user").pop()?.content;
        return (
          <div className="flex gap-2.5 animate-in fade-in duration-300">
            <div className={cn("w-7 h-7 rounded-full overflow-hidden shrink-0 ring-2 mt-0.5", ts.ring)}>
              <img src={BOT_AVATAR[thinkBot2] || `/agents/${thinkBot2.toLowerCase()}.png`} alt="" className="w-full h-full object-cover" />
            </div>
            <div className={cn("border-l-[3px] border border-gray-200 rounded-xl rounded-tl-none px-3.5 py-2.5 shadow-sm", ts.border, ts.bubble)}>
              <div className={cn("flex items-center gap-2 text-sm", ts.text)}>
                {(() => { const DIcon = DEPT_DASH_ICON[thinkBot2] || Bot; return <DIcon className="h-4 w-4 shrink-0 animate-pulse" />; })()}
                <span className="font-medium">{botName2}</span>
                <ThinkingLabel botCode={thinkBot2} userText={lastUserMsg} />
              </div>
            </div>
          </div>
        );
      })()}

      {/* S231 lot Z2 (ZONES.CONNECTEES.1, DEC-HE2T-1 Q9 b) : la carte d'action du geste en attente, sous la dernière bulle, à part des
          options de CarlOS ; elle reste tant que /elements la rend (carte_action), même sous des bulles plus récentes */}
      {activeThreadId && discussionElementsLocal?.carte_action
        && !(discussionElementsLocal.carte_action.geste === "decideur" && nomEnCours(discussionElementsLocal.carte_action.element_id)) && (
        <CarteAction carte={discussionElementsLocal.carte_action} inactif={isTyping}
          onBouton={(b) => { void cliquerBoutonCarte(activeThreadId, discussionElementsLocal.carte_action!, b); }} />
      )}

      <div ref={endRef} />
    </div>
  );
}

// ═══ CONFIER BUBBLE BUTTON — C.BUBBLE.EXEC.1 (S162) ═══
// Bouton inline sous les bulles bot : assigner le contenu à la file d'exécution
function ConfierBubbleButton({ botCode, messageContent, threadId, onAssign }: {
  botCode: string;
  messageContent: string;
  threadId: string;
  onAssign: (task: Omit<TaskItem, "id" | "createdAt" | "status">) => string;
}) {
  const [level, setLevel] = useState<"observer" | "copilot" | "autopilot">("copilot");
  const [assigned, setAssigned] = useState(false);

  if (assigned) {
    return (
      <div className="flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-lg bg-green-50 border border-green-200 text-[10px] text-green-600 font-medium w-fit">
        <CheckCircle2 className="h-2.5 w-2.5" />
        Assigné à la file
      </div>
    );
  }

  const label = messageContent.split("\n")[0].slice(0, 60) || "Tâche de discussion";

  return (
    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
      <span className="text-[9px] text-gray-400 uppercase tracking-wide font-medium">Confier :</span>
      {(["observer", "copilot", "autopilot"] as const).map(l => (
        <button
          key={l}
          onClick={() => setLevel(l)}
          className={cn(
            "text-[9px] px-1.5 py-0.5 rounded border transition-all",
            level === l
              ? "bg-indigo-100 border-indigo-300 text-indigo-700"
              : "bg-gray-50 border-gray-200 text-gray-500 hover:border-gray-300"
          )}
        >{l}</button>
      ))}
      <button
        onClick={() => {
          onAssign({ botCode, label, prompt: messageContent, autonomyLevel: level, sourceContext: { surface: "bubble", threadId } });
          setAssigned(true);
        }}
        className="flex items-center gap-1 px-2 py-0.5 rounded-lg bg-indigo-600 text-white text-[10px] font-medium hover:bg-indigo-700 transition-colors"
      >
        <Zap className="h-2.5 w-2.5" />
        {BOT_NAME[botCode] || botCode}
      </button>
    </div>
  );
}

// ═══ AGENT SELECTOR — dropdown d'ajout d'agents dans le header ═══
function AgentSelector({ activeRoster, addBotToRoster, removeBotFromRoster, onConsultExpert }: {
  activeRoster: string[];
  addBotToRoster: (code: string) => void;
  removeBotFromRoster: (code: string) => void;
  onConsultExpert?: (code: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors cursor-pointer"
        title="Ajouter un agent"
      >
        <Plus className="h-3 w-3 text-white/70" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 w-72 bg-white rounded-2xl border border-gray-200 shadow-xl py-2 z-30 max-h-[420px] overflow-auto">
          <div className="px-4 py-2 flex items-center gap-2 border-b border-gray-100 mb-1">
            <Users className="h-3.5 w-3.5 text-violet-500" />
            <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Brain Team</span>
            <span className="text-[9px] text-gray-400 ml-auto">{activeRoster.length} actifs</span>
          </div>
          {BOT_CODES.map((code) => {
            const inRoster = activeRoster.includes(code);
            const st = V3_STYLE[code] || DEFAULT_STYLE;
            return (
              <button
                key={code}
                onClick={() => {
                  if (inRoster) {
                    removeBotFromRoster(code);
                  } else {
                    addBotToRoster(code);
                    onConsultExpert?.(code);
                  }
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2.5 transition-all cursor-pointer text-left group/agent mx-1 rounded-xl",
                  inRoster
                    ? "bg-blue-50/70 hover:bg-blue-50"
                    : "hover:bg-gray-50"
                )}
                style={{ width: "calc(100% - 8px)" }}
              >
                <div className={cn("w-9 h-9 rounded-full overflow-hidden shrink-0 ring-2 shadow-sm transition-all group-hover/agent:shadow-md", inRoster ? "ring-blue-400" : st.ring)}>
                  <img src={BOT_AVATAR[code] || `/agents/${code.toLowerCase()}.png`} alt={BOT_NAME[code]} className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-[12px] font-bold text-gray-900 truncate">{BOT_NAME[code]}</span>
                    <span className={cn(
                      "text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide shrink-0",
                      inRoster ? "bg-blue-200 text-blue-700" : "bg-gray-100 text-gray-500"
                    )}>{BOT_ROLE[code]?.split(" ")[0] || ""}</span>
                  </div>
                  <span className="text-[10px] text-gray-400 block truncate">{BOT_ROLE[code]}</span>
                </div>
                {inRoster ? (
                  <div className="shrink-0 w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center shadow-sm">
                    <Check className="h-3 w-3 text-white" />
                  </div>
                ) : (
                  <div className="shrink-0 w-5 h-5 rounded-full border-2 border-gray-200 group-hover/agent:border-blue-300 transition-colors" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══ DEPT WELCOME SCREEN — accueil dynamique par département ═══
function DeptWelcomeScreen({ botCode, onAction, onResumeThread, onDeleteThread, onRenameThread, threads }: {
  botCode: string;
  onAction: (text: string, phase?: PhaseKey) => void;
  onResumeThread: (threadId: string) => void;
  onDeleteThread: (threadId: string) => void;
  onRenameThread: (threadId: string, newTitle: string) => void;
  threads: Array<{ id: string; title: string; primaryBot?: string; createdAt?: string; updatedAt?: string; status?: string; workPhase?: string }>;
}) {
  const DeptIcon = DEPT_DASH_ICON[botCode] || Bot;
  const gradient = DEPT_GRADIENT[botCode] || "from-blue-700 to-blue-500";
  const greeting = DEPT_GREETING[botCode] || "Comment puis-je t'aider?";
  const actions = DEPT_ACTIONS[botCode] || [];
  const botName = BOT_NAME[botCode] || "CarlOS";
  const botDisplay = BOT_DISPLAY[botCode];
  const recentThreads = threads;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  return (
    <div className="flex justify-center py-10">
      <div className="text-center space-y-5 max-w-lg w-full">
        {/* Bot icon with department gradient */}
        <div className={cn("inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br shadow-lg", gradient)}>
          <DeptIcon className="h-8 w-8 text-white" />
        </div>

        {/* Greeting */}
        <div>
          <h3 className="text-lg font-bold text-gray-800">{botName}</h3>
          {botDisplay && <p className="text-[10px] text-gray-400 font-medium mt-0.5">{botDisplay.role} — {botDisplay.dept}</p>}
          <p className="text-sm text-gray-500 mt-2 leading-relaxed">{greeting}</p>
        </div>

        {/* Action buttons — style pilules colorées (SuggestionsWelcome pattern) */}
        {actions.length > 0 && (
          <div className="flex flex-wrap gap-2 justify-center">
            {actions.map((action) => {
              const colors = ACTION_COLORS[action.color] || ACTION_COLORS.blue;
              return (
                <button
                  key={action.label}
                  onClick={() => onAction(action.description, action.phase)}
                  className={cn(
                    "flex items-center gap-1.5 text-xs px-3 py-2 rounded-xl border font-medium transition-colors cursor-pointer",
                    colors
                  )}
                >
                  <action.icon className="h-3.5 w-3.5" />
                  {action.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Recent threads — cards with CREDO phase indicator */}
        {recentThreads.length > 0 && (
          <div className="pt-4 text-left max-w-md mx-auto w-full">
            <div className="flex items-center gap-1.5 mb-3 px-1">
              <Clock className="h-3 w-3 text-gray-400" />
              <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Reprendre une discussion</span>
            </div>
            <div className="space-y-2">
              {recentThreads.map((thread) => {
                // 5 phases de travail: discussion, reflexion, creation (conception), execution, retroaction
                // Default à "discussion" si la phase n'est pas définie (threads créés avant le feature)
                const phaseKey = thread.workPhase || "discussion";
                const phaseData = PHASE_COLORS[phaseKey as keyof typeof PHASE_COLORS] || PHASE_COLORS.discussion;
                // "parked" = état normal (thread mis de côté) — ne pas afficher. Seulement "Terminée" est pertinent.
                const statusLabel = thread.status === "completed" ? "Terminée" : null;
                const statusStyle = thread.status === "completed" ? "bg-green-100 text-green-600" : "";
                return (
                  <button
                    key={thread.id}
                    onClick={() => onResumeThread(thread.id)}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all cursor-pointer text-left group"
                  >
                    {/* Phase dot — seulement si la phase existe sur le thread */}
                    {phaseData ? (
                      <div className={cn("w-2.5 h-2.5 rounded-full shrink-0", phaseData.dot)} title={phaseData.label} />
                    ) : (
                      <MessageCircle className="h-3.5 w-3.5 text-gray-300 shrink-0" />
                    )}
                    {/* Thread info */}
                    <div className="flex-1 min-w-0">
                      {editingId === thread.id ? (
                        <input
                          autoFocus
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              const trimmed = editValue.trim();
                              if (trimmed && trimmed !== thread.title) onRenameThread(thread.id, trimmed);
                              setEditingId(null);
                            } else if (e.key === "Escape") {
                              setEditingId(null);
                            }
                          }}
                          onBlur={() => {
                            const trimmed = editValue.trim();
                            if (trimmed && trimmed !== thread.title) onRenameThread(thread.id, trimmed);
                            setEditingId(null);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="text-xs font-medium text-gray-700 w-full bg-white border border-blue-300 rounded px-1.5 py-0.5 outline-none focus:ring-1 focus:ring-blue-400"
                        />
                      ) : (
                        <span className="text-xs font-medium text-gray-700 block truncate">{thread.title || "Discussion sans titre"}</span>
                      )}
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {phaseData && <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full font-medium", phaseData.badge)}>{phaseData.label}</span>}
                        {statusLabel && (
                          <span className={cn("text-[9px] px-1.5 py-0.5 rounded-full font-medium", statusStyle)}>{statusLabel}</span>
                        )}
                        {(thread.updatedAt || thread.createdAt) && (() => {
                          const dateStr = thread.updatedAt || thread.createdAt!;
                          const d = new Date(dateStr);
                          if (isNaN(d.getTime())) return null;
                          const now = Date.now();
                          const diffMs = now - d.getTime();
                          const diffMin = Math.floor(diffMs / 60000);
                          const diffH = Math.floor(diffMs / 3600000);
                          const diffD = Math.floor(diffMs / 86400000);
                          let label: string;
                          if (diffMin < 1) label = "a l'instant";
                          else if (diffMin < 60) label = `il y a ${diffMin} min`;
                          else if (diffH < 24) label = `il y a ${diffH} h`;
                          else if (diffD < 7) label = `il y a ${diffD} j`;
                          else label = d.toLocaleDateString("fr-CA", { day: "numeric", month: "short" });
                          return <span className="text-[9px] text-gray-400">{label}</span>;
                        })()}
                      </div>
                    </div>
                    {/* Rename button on hover */}
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditValue(thread.title || ""); setEditingId(thread.id); }}
                      className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-blue-100 transition-all cursor-pointer shrink-0"
                      title="Renommer"
                    >
                      <Pencil className="h-3 w-3 text-blue-400" />
                    </button>
                    {/* Delete button on hover */}
                    <button
                      onClick={(e) => { e.stopPropagation(); onDeleteThread(thread.id); }}
                      className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-red-100 transition-all cursor-pointer shrink-0"
                      title="Supprimer"
                    >
                      <X className="h-3 w-3 text-red-400" />
                    </button>
                    {/* Arrow indicator */}
                    <ChevronRight className="h-3.5 w-3.5 text-gray-300 group-hover:text-gray-500 transition-colors shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export function DiscussionWindow() {
  const { demoSimId } = useDemo();

  // Mode demo actif → afficher DemoChatPlayer a la place du vrai chat
  if (demoSimId) {
    return <DemoChatPlayer />;
  }

  return <DiscussionWindowInner />;
}

function DiscussionWindowInner() {
  const { cockpitTab, activeBotCode, activePhase, setActivePhase, setRightSection, setReflexionContext, setFocusType, setActiveDeliverable, credoPhase, reflexionContext, addWorkflowItem, activeMeeting: dwActiveMeeting, addWorkspaceBlock, removeWorkspaceBlock, workspaceBlocks, chatStage, setReflexionSetup } = useAmorcer();
  const { activeRoster, addBotToRoster, removeBotFromRoster, messages, sendMessage, sendMultiPerspective, threads, resumeThread, deleteThread, renameThread, chatTargetBot, activeThreadId, commandMission, clearCommandMission, setReflectionMode, isTyping } = useChatContext();
  const isMobile = useIsMobile();
  const isOrbit9 = cockpitTab === "orbit9";
  const isEmpty = messages.length === 0;
  const discussionElements = useDiscussionElements(activeThreadId, isTyping);
  const ecranGreffierActif = discussionElements?.ecran_greffier && discussionElements?.a_elements;

  // C.55 — COMMAND bandeau
  const [missionLaunching, setMissionLaunching] = useState(false);
  const commandProposal = messages.length > 0
    ? [...messages].reverse().find(m => m.metadata?.command_available === true)
    : null;

  // C.56 — ChantierSuggestionModal DÉSACTIVÉ: Carl ne veut pas de modal.
  // Il existe déjà une vue chantier et une section chantier dans la sidebar.
  // La navigation vers le chantier se fait via la sidebar, pas par modal automatique.
  const showChantierModal = false;
  const chantierMatches: { chantier_id: number; titre: string; score: number; reason: string; status: string; section_primaire: string }[] = [];

  // S117: Lifted ControlPanel state — shared with ChatBoxV3 buttons
  const [cpOpen, setCpOpen] = useState(false);
  const [cpTab, setCpTab] = useState<"modes" | "agents" | "reunion">("modes");

  // FILS.VIDES.LISTE (S225) : « Reprendre une discussion » ne liste que les fils qui ont au moins un message — dans le navigateur, ou sur le
  // serveur (message_count) pour les fils relus de l'API sans leurs messages ; un fil vide né d'un changement de département n'y est pas
  const [messagesServeur, setMessagesServeur] = useState<Record<string, number>>({});
  // TITRE.FIL.1 : titres rédigés par le serveur à partir du seul fil (contexte.titre_serveur), affichés dès qu'ils existent
  const [titresServeur, setTitresServeur] = useState<Record<string, string>>({});
  useEffect(() => {
    let annule = false;
    api.listDiscussions().then((ds) => {
      if (annule || !ds) return;
      setMessagesServeur(Object.fromEntries(ds.map((d) => [d.external_id, d.message_count || 0])));
      setTitresServeur(Object.fromEntries(ds.flatMap((d) => {
        const t = (d as { contexte?: { titre_serveur?: { titre?: string } } }).contexte?.titre_serveur?.titre;
        return t ? [[d.external_id, t]] : [];
      })));
    }).catch(() => { /* liste serveur indisponible : seuls les fils qui ont des messages dans le navigateur */ });
    return () => { annule = true; };
  }, [activeBotCode, isEmpty]);

  // W.1 Expert pipeline — disponible ici pour passer à ControlPanel (LOT1 fix)
  const _credoStepsDW = ["comprendre", "rechercher", "exposer", "demontrer", "objectif"];
  const _workspacePhaseDW = activePhase === "discussion" && chatStage < _credoStepsDW.length
    ? `discussion_${_credoStepsDW[chatStage]}`
    : activePhase;
  const addExpertToWorkspaceDW = useCallback(async (code: string) => {
    addBotToRoster(code);
    const credoStep = (workspaceBlocks.length > 0
      ? workspaceBlocks[workspaceBlocks.length - 1].credo_step
      : "C") as "C" | "R" | "E" | "D" | "O";
    const tempId = `catching-up-${code}-${Date.now()}`;
    addWorkspaceBlock({
      id: tempId, type: "catching_up",
      title: `${BOT_NAME[code] || code} prend connaissance...`,
      summary: "Analyse de la discussion en cours...",
      structured_data: {}, credo_step: credoStep,
      credo_sub_section: "experts", confidence: 0.5,
      source: code, sourceType: "chat", timestamp: Date.now(), is_catching_up: true,
    } as any);
    try {
      const lastUserMsg = messages.filter(m => m.role === "user").pop()?.content || "";
      const lastBotMsg = messages.filter(m => m.role === "assistant" && m.content).pop()?.content || "";
      const recentCtx = workspaceBlocks.slice(-3)
        .map(b => `[${BOT_NAME[b.source || ""] || ""}] ${b.title}: ${(b.summary || "").substring(0, 150)}`)
        .join("\n");
      const context = lastUserMsg || lastBotMsg
        ? `Question: ${lastUserMsg}\n\nAnalyse en cours: ${lastBotMsg.substring(0, 500)}\n\nContexte récent:\n${recentCtx}`
        : `Présente ce que tu apportes à cette discussion (3-4 phrases). Sois direct et propose 2-3 pistes concrètes.`;
      const res = await api.chatMulti({
        message: context, user_id: 1, agents: [code], primary_agent: code,
        workspace_phase: _workspacePhaseDW,
      });
      const persp = res.perspectives?.[0];
      if (persp) {
        const _MARKER_RE = /^\[(EQUIPE PRESENTE|BOT PRINCIPAL|CONSULTANT\s*[—\-])[^\]]*\]\s*/gim;
        const cleanContent = (persp.contenu || "").replace(_MARKER_RE, "").replace(/^---+\s*/gm, "").trim();
        if (cleanContent.length > 10) {
          crystallizeAndAddBlock(cleanContent, {
            addWorkspaceBlock, botCode: code, credoStep,
            credo_sub_section: "experts", confidence: 0.75,
            forceTitle: generateExpertBlockTitle(BOT_NAME[code] || code, cleanContent, "Apport expert"),
            replace_block_id: tempId, blockId: `expert-${code}-${Date.now()}`,
          });
        } else { removeWorkspaceBlock(tempId); }
      } else { removeWorkspaceBlock(tempId); }
    } catch (err) {
      console.error("[ExpertAdd]", err);
      removeWorkspaceBlock(tempId);
    }
  }, [addBotToRoster, messages, workspaceBlocks, addWorkspaceBlock, removeWorkspaceBlock, _workspacePhaseDW]);

  // URL-aware thread sync — quand l'URL contient /discussion/thread-XXXX,
  // charger ce thread si ce n'est pas déjà le thread actif
  const d9EssayeRef = useRef<string | null>(null);
  useEffect(() => {
    const path = window.location.pathname;
    const match = path.match(/\/discussion\/(thread-[a-zA-Z0-9_-]+)/);
    if (!match) return;
    const urlThreadId = match[1];
    if (urlThreadId && urlThreadId !== activeThreadId) {
      // Guard: si on a déjà un thread actif avec messages, l'URL est périmée (race condition)
      if (activeThreadId && messages.length > 0) return;
      const found = threads.find(t => t.id === urlThreadId);
      if (found) {
        resumeThread(urlThreadId, activePhase);
      } else if (d9EssayeRef.current !== urlThreadId) {
        // P-S232-31 n° 3 (D9, parade v3) : après un rechargement, l'adresse fait foi même si la liste des fils n'est pas encore là ; une
        // seule tentative par adresse, et la colonne de droite relit /elements
        d9EssayeRef.current = urlThreadId;
        resumeThread(urlThreadId, activePhase);
        window.dispatchEvent(new CustomEvent(EVT_RELIRE_ELEMENTS));
      }
    }
  }, [threads, activeThreadId, activePhase, resumeThread, messages.length]);

  // MOBILE.WORKSPACE.SUIVI.1 — reprise d'une discussion dont les messages arrivent du serveur apres le clic :
  // le workspace bascule quand les messages sont la (jamais pour un fil vide)
  const pendingResumeRef = useRef<{ id: string; title: string } | null>(null);
  useEffect(() => {
    const pending = pendingResumeRef.current;
    if (!pending) return;
    if (activeThreadId === pending.id && messages.length > 0) {
      pendingResumeRef.current = null;
      if (pending.title) {
        setReflexionContext(pending.title);
        setFocusType("chantier");
      }
      setActivePhase("discussion" as any);
      setRightSection(null);
    }
  }, [activeThreadId, messages.length, setReflexionContext, setFocusType, setActivePhase, setRightSection]);

  // Expert consultation moved to workspace SuggestedExpertsPanel (LiveDiscussionView)

  // Reset workspace au cockpit quand la discussion est vide (refresh, nouveau thread)
  // Évite d'avoir une phase orpheline sans messages
  // GUARD: ne PAS reset pendant un meeting actif (la discussion est vide au début, le transcript arrive après)
  useEffect(() => {
    if (isEmpty && activePhase && !dwActiveMeeting && !["observation", "attention", "moderation", "execution", "retroaction"].includes(activePhase)) {
      // P-S232-43 D7 : un fil ouvert à une adresse /…/discussion/… n'est jamais renvoyé au Cockpit (avant : seulement si l'adresse portait « thread- »)
      if (activeThreadId && window.location.pathname.includes("/discussion/")) return;
      setActivePhase("observation" as PhaseKey);
      setReflexionContext(null);
      setRightSection("cockpit");
      setReflexionSetup(null);
      setReflectionMode("credo");
    }
  }, [isEmpty]);

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header UB_BLUE h-12 — design modélisé V3 */}
      <div className={cn("h-12 shrink-0 flex items-center gap-2 bg-[#073E5A]", "px-3")}>
        {isOrbit9 ? (
          <>
            <Atom className="h-4 w-4 text-white" />
            <span className="text-[11px] text-white font-medium">Orbit<sup className="text-[8px]">9</sup></span>
            <div className="flex-1" />
          </>
        ) : (
          <>
            <BrainCog className="h-4 w-4 text-white" />
            <span className="text-[11px] text-white font-medium">Brain Team</span>
            <div className="flex-1" />

            {/* DEC-INDICATEUR-BARRE-1 (Carl, S220 ; DEC-ECRAN-CORRECTIFS-1, S221) : plus d'indicateur des 5 étapes dans cette barre ; il reste dans
                l'en-tête du workspace et le menu Étapes (test : tests/e2e/test_barre_discussion_reel.py) */}
            {/* P-S232-43 D5 : un bouton « Nouvelle discussion » (newConversation()) a été essayé ici puis retiré — sur un fil ouvert, newConversation()
                ne change ni le fil actif, ni l'adresse, ni les bulles (sonde du 4 nov. 21 h 00, compte 1015 ; cause dans v2, hors lecture) ;
                un bouton sans effet est interdit. Parade de la démo : bouton Cockpit, puis écrire (test_cockpit_discussion_reel.py). */}

            {/* Primary bot avatar + roster chips + AgentSelector (+) */}
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full overflow-hidden ring-1 ring-white/30 shrink-0">
                <img src={BOT_AVATAR[activeBotCode] || `/agents/${activeBotCode.toLowerCase()}.png`} alt={BOT_NAME[activeBotCode] || activeBotCode} className="w-full h-full object-cover" />
              </div>
              <span className="text-[9px] text-white/80 font-medium">{BOT_NAME[activeBotCode] || activeBotCode}</span>
              {/* C.53 — Roster chips for added bots */}
              {activeRoster.filter(c => c !== activeBotCode).slice(0, 2).map(code => (
                <div key={code} className="w-5 h-5 rounded-full overflow-hidden ring-1 ring-white/30 shrink-0 -ml-1" title={BOT_NAME[code] || code}>
                  <img src={BOT_AVATAR[code] || `/agents/${code.toLowerCase()}.png`} alt={code} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>

          </>
        )}
      </div>

      {/* C.55 — Bandeau COMMAND (proposition bot OU mission active) */}
      {commandProposal && !(commandMission && !commandMission.completed) && (
        <div className="shrink-0 px-3 py-1.5 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
          <Zap className="h-3.5 w-3.5 text-amber-600 shrink-0" />
          <span className="text-[11px] text-amber-800 flex-1 truncate">
            Mode Équipe recommandé — {(commandProposal.metadata?.scan_bots as string[] | undefined)?.join(", ")}
          </span>
          <button
            disabled={missionLaunching}
            onClick={async () => {
              setMissionLaunching(true);
              try {
                const lastUserMsg = [...messages].reverse().find(m => m.role === "user");
                if (lastUserMsg) {
                  await api.commandStart(
                    lastUserMsg.content,
                    ((commandProposal.metadata?.urgency) as string | undefined) || "routine",
                    (commandProposal.metadata?.scan_bots) as string[] | undefined,
                  );
                }
              } catch (e) {
                console.error("[C.55] commandStart error", e);
              } finally {
                setMissionLaunching(false);
              }
            }}
            className="shrink-0 text-[10px] font-medium px-2 py-0.5 rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
          >
            {missionLaunching ? "Lancement…" : "Lancer Mission"}
          </button>
        </div>
      )}
      {commandMission && !commandMission.completed && (
        <div className="shrink-0 px-3 py-1.5 bg-blue-50 border-b border-blue-200 flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-blue-500 animate-pulse shrink-0" />
          <span className="text-[11px] text-blue-800 flex-1 truncate">
            Mission COMMAND en cours — Étape: {commandMission.stage}
          </span>
          <button onClick={clearCommandMission} className="shrink-0 text-[10px] text-blue-400 hover:text-blue-700">✕</button>
        </div>
      )}

      {/* Zone principale: DeptWelcomeScreen quand vide, V3MessageList sinon */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {isEmpty ? (
          <div className="flex-1 overflow-auto">
            <DeptWelcomeScreen
              botCode={activeBotCode}
              onAction={(text, phase) => {
                sendMessage(text, chatTargetBot, undefined, { workspacePhase: phase || "discussion" });
                setReflexionContext(text.substring(0, 80));
                setFocusType("chantier");
                setRightSection(null);
                // Phase explicite (du bouton) OU discussion par defaut — JAMAIS d'auto-détection
                const targetPhase = phase || "discussion";
                setActivePhase(targetPhase as any);
              }}
              onResumeThread={(threadId) => {
                const thread = threads.find(t => t.id === threadId);
                resumeThread(threadId, activePhase);

                // Push URL pour que le guard reset useEffect (L2018) ne redirige pas vers cockpit
                const dept = window.location.pathname.split('/')[1] || 'ceo';
                window.history.pushState({}, '', `/${dept}/discussion/${threadId}`);

                const context = thread?.title || "";

                // N'activer workspace + discussion que si le thread a des messages —
                // sinon le workspace s'allume sur un thread vide et le chat reste sur l'écran d'accueil
                if (thread?.messages && thread.messages.length > 0) {
                  pendingResumeRef.current = null;
                  if (context) {
                    setReflexionContext(context);
                    setFocusType("chantier");
                  }
                  setActivePhase("discussion" as any);
                  setRightSection(null);
                } else {
                  pendingResumeRef.current = { id: threadId, title: context };
                }
              }}
              onDeleteThread={(threadId) => deleteThread(threadId)}
              onRenameThread={(threadId, newTitle) => renameThread(threadId, newTitle)}
              threads={threads.filter((t) => t.primaryBot === activeBotCode
                && ((t.messages?.length || 0) > 0 || (messagesServeur[t.id] || 0) > 0)).sort((a, b) => {
                const da = new Date(a.updatedAt || a.createdAt || 0).getTime();
                const db = new Date(b.updatedAt || b.createdAt || 0).getTime();
                return db - da;
              }).map((t) => titresServeur[t.id] ? { ...t, title: titresServeur[t.id] } : t)}
            />
          </div>
        ) : (
          <V3MessageList />
        )}
      </div>

      {/* S117 Phase 3A: ControlPanel retractable — Modes / Agents / Reunion */}
      <ControlPanel isOpen={cpOpen} setIsOpen={setCpOpen} activeTab={cpTab} setActiveTab={setCpTab} onAddExpert={addExpertToWorkspaceDW} />

      {/* ChatBox V3 — design Claude AI (SimAmorcer L676-754) branché sur sendMessage réel */}
      <ChatBoxV3 onOpenPanel={(tab: "modes" | "agents" | "reunion") => {
        if (cpOpen && cpTab === tab) { setCpOpen(false); }
        else { setCpTab(tab); setCpOpen(true); }
      }} />

      {/* C.56 — ChantierSuggestionModal: monté quand Phase O (chatStage 4) */}
      {showChantierModal && (
        <ChantierSuggestionModal
          open={showChantierModal}
          onClose={() => setShowChantierModal(false)}
          matches={chantierMatches}
          workspaceBlocks={workspaceBlocks as { type: string; content: string; title?: string }[]}
          threadId={activeThreadId || ""}
          botCode={activeBotCode}
          onCreated={() => setShowChantierModal(false)}
          onJoined={() => setShowChantierModal(false)}
        />
      )}
    </div>
  );
}

// ═══ S117 Phase 3A: CONTROL PANEL — Footer retractable (Modes / Agents / Reunion) ═══
// Refonte v2: qualification flow, orange theme, no scroll agents, workspace routing

const REFLEXION_MODES: { id: string; label: string; icon: LucideIcon; color: string; prompt: string }[] = [
  { id: "brainstorm", label: "Brainstorm", icon: Lightbulb, color: "amber", prompt: "Lance un brainstorm créatif tous azimuts sur: " },
  { id: "analyser", label: "Analyser", icon: Eye, color: "blue", prompt: "Analyse approfondie structurée de: " },
  { id: "challenger", label: "Challenger", icon: Shield, color: "red", prompt: "Joue l'avocat du diable, challenge cette approche et trouve les failles de: " },
  { id: "debat", label: "Debat", icon: Swords, color: "rose", prompt: "Lance un debat structure pour/contre sur: " },
  { id: "deep_search", label: "Deep Search", icon: Globe, color: "cyan", prompt: "Recherche approfondie — tendances, benchmarks, meilleures pratiques pour: " },
  { id: "decision", label: "Decision", icon: CheckCircle2, color: "emerald", prompt: "Aide-moi a prendre une decision structuree sur: " },
  { id: "strategie", label: "Strategie", icon: Target, color: "purple", prompt: "Analyse strategique complete (SWOT, positionnement, recommandations) sur: " },
  { id: "crise", label: "Crise", icon: Zap, color: "orange", prompt: "Evaluation de crise et plan d'action 48h pour: " },
  { id: "innovation", label: "Innovation", icon: Sparkles, color: "pink", prompt: "Exploration innovation — tendances, disruption, opportunites pour: " },
];

const MODE_BUTTON_STYLES: Record<string, { bg: string; text: string; border: string; hover: string; iconColor: string }> = {
  amber:   { bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200",   hover: "hover:bg-amber-100 hover:border-amber-300",     iconColor: "text-amber-500" },
  blue:    { bg: "bg-blue-50",    text: "text-blue-700",    border: "border-blue-200",    hover: "hover:bg-blue-100 hover:border-blue-300",       iconColor: "text-blue-500" },
  red:     { bg: "bg-red-50",     text: "text-red-700",     border: "border-red-200",     hover: "hover:bg-red-100 hover:border-red-300",         iconColor: "text-red-500" },
  rose:    { bg: "bg-rose-50",    text: "text-rose-700",    border: "border-rose-200",    hover: "hover:bg-rose-100 hover:border-rose-300",       iconColor: "text-rose-500" },
  cyan:    { bg: "bg-cyan-50",    text: "text-cyan-700",    border: "border-cyan-200",    hover: "hover:bg-cyan-100 hover:border-cyan-300",       iconColor: "text-cyan-500" },
  emerald: { bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200", hover: "hover:bg-emerald-100 hover:border-emerald-300", iconColor: "text-emerald-500" },
  purple:  { bg: "bg-purple-50",  text: "text-purple-700",  border: "border-purple-200",  hover: "hover:bg-purple-100 hover:border-purple-300",   iconColor: "text-purple-500" },
  orange:  { bg: "bg-orange-50",  text: "text-orange-700",  border: "border-orange-200",  hover: "hover:bg-orange-100 hover:border-orange-300",   iconColor: "text-orange-500" },
  pink:    { bg: "bg-pink-50",    text: "text-pink-700",    border: "border-pink-200",    hover: "hover:bg-pink-100 hover:border-pink-300",       iconColor: "text-pink-500" },
};

const PLAYBOOKS: { id: string; label: string; icon: LucideIcon; bots: string[]; desc: string; color: string; stages: string[] }[] = [
  { id: "libre", label: "Libre", icon: Video, bots: [], desc: "Sans format", color: "blue", stages: [] },
  { id: "board", label: "Board / CA", icon: Crown, bots: ["CEOB", "CFOB", "CSOB"], desc: "PV officiel, decisions", color: "indigo", stages: ["Ouverture", "Points a l'ordre", "Varia", "Cloture"] },
  { id: "client", label: "Rencontre Client", icon: Users, bots: ["CEOB", "CROB", "CMOB"], desc: "Discovery, objections", color: "emerald", stages: ["Decouverte", "Besoins", "Objections", "Conclusion"] },
  { id: "brainstorm", label: "Brainstorm", icon: Lightbulb, bots: ["CEOB", "CTOB", "CMOB"], desc: "Divergence → convergence", color: "amber", stages: ["Divergence", "Tri", "Convergence"] },
  { id: "crise", label: "Crise", icon: AlertTriangle, bots: ["CEOB", "COOB", "CFOB"], desc: "Evaluation, plan 48h", color: "red", stages: ["Evaluation", "Plan d'action", "Suivi"] },
  { id: "diagnostic", label: "Diagnostic VITAA", icon: Search, bots: ["CEOB", "COOB"], desc: "Etat des lieux", color: "sky", stages: ["Etat des lieux", "Analyse", "Recommandations"] },
  { id: "travail", label: "Reunion de travail", icon: Pencil, bots: ["CEOB"], desc: "Session de travail", color: "gray", stages: ["Objectifs", "Travail", "Recap"] },
  { id: "podcast", label: "Podcast", icon: Mic, bots: ["CEOB", "CMOB"], desc: "Co-animation", color: "pink", stages: ["Intro", "Discussion", "Outro"] },
  { id: "debat", label: "Debat", icon: Swords, bots: ["CEOB", "CSOB", "CMOB"], desc: "Pour/contre, verdict", color: "orange", stages: ["These", "Antithese", "Synthese"] },
  { id: "standup", label: "Stand-up", icon: Timer, bots: ["CEOB", "COOB"], desc: "Sync quotidien rapide", color: "cyan", stages: ["Tour de table", "Blocages", "Prochaines etapes"] },
  { id: "onboarding", label: "Onboarding", icon: UserPlus, bots: ["CEOB", "CHROB"], desc: "Accueil nouvel employe", color: "teal", stages: ["Presentation", "Culture", "Formation", "Plan 90j"] },
  { id: "formation", label: "Formation", icon: GraduationCap, bots: ["CEOB", "CHROB", "CTOB"], desc: "Session d'apprentissage", color: "violet", stages: ["Objectifs", "Contenu", "Exercices", "Evaluation"] },
  { id: "negociation", label: "Negociation", icon: Handshake, bots: ["CEOB", "CROB", "CFOB"], desc: "Fournisseur, partenaire", color: "lime", stages: ["Preparation", "Offre", "Contre-offre", "Accord"] },
  { id: "retro", label: "Retrospective", icon: RotateCcw, bots: ["CEOB", "COOB", "CTOB"], desc: "Bilan et amelioration", color: "purple", stages: ["Bilan", "Problemes", "Actions"] },
  { id: "pitch", label: "Pitch", icon: Presentation, bots: ["CEOB", "CMOB", "CFOB"], desc: "Investisseur, client", color: "blue", stages: ["Accroche", "Probleme", "Solution", "Ask"] },
  { id: "one_on_one", label: "One-on-one", icon: UserCheck, bots: ["CEOB"], desc: "Rencontre individuelle", color: "rose", stages: ["Check-in", "Objectifs", "Feedback"] },
  { id: "revue_finance", label: "Revue financiere", icon: DollarSign, bots: ["CEOB", "CFOB", "CROB"], desc: "Budget, resultats, previsions", color: "green", stages: ["Resultats", "Budget", "Previsions", "Decisions"] },
];

// colorName used to derive Tailwind classes: bg-{colorName}-50, border-{colorName}-200, text-{colorName}-700
const BOT_LIST_CP = [
  { code: "CEOB", name: "CarlOS", role: "CEO", color: "bg-sky-500", colorName: "sky" },
  { code: "CTOB", name: "Tim", role: "CTO", color: "bg-violet-500", colorName: "violet" },
  { code: "CFOB", name: "Frank", role: "CFO", color: "bg-emerald-500", colorName: "emerald" },
  { code: "CMOB", name: "Mathilde", role: "CMO", color: "bg-pink-500", colorName: "pink" },
  { code: "CSOB", name: "Simone", role: "CSO", color: "bg-red-500", colorName: "red" },
  { code: "COOB", name: "Olivier", role: "COO", color: "bg-orange-500", colorName: "orange" },
  { code: "CPOB", name: "Paco", role: "CPO", color: "bg-sky-600", colorName: "sky" },
  { code: "CHROB", name: "Helene", role: "CHRO", color: "bg-amber-500", colorName: "amber" },
  { code: "CROB", name: "Rich", role: "CRO", color: "bg-lime-600", colorName: "lime" },
  { code: "CISOB", name: "Sebastien", role: "CISO", color: "bg-slate-600", colorName: "slate" },
  { code: "CLOB", name: "Loulou", role: "CLO", color: "bg-indigo-500", colorName: "indigo" },
  { code: "CINOB", name: "Ines", role: "CINO", color: "bg-teal-500", colorName: "teal" },
];

// Tailwind color class map for dynamic bot-colored pills (all classes explicit for JIT)
const BOT_PILL_STYLES: Record<string, { bg: string; border: string; text: string; textLight: string; ring: string }> = {
  sky: { bg: "bg-sky-50", border: "border-sky-200", text: "text-sky-700", textLight: "text-sky-400", ring: "ring-sky-200" },
  violet: { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", textLight: "text-violet-400", ring: "ring-violet-200" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", textLight: "text-emerald-400", ring: "ring-emerald-200" },
  pink: { bg: "bg-pink-50", border: "border-pink-200", text: "text-pink-700", textLight: "text-pink-400", ring: "ring-pink-200" },
  red: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", textLight: "text-red-400", ring: "ring-red-200" },
  orange: { bg: "bg-orange-50", border: "border-orange-200", text: "text-orange-700", textLight: "text-orange-400", ring: "ring-orange-200" },
  amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", textLight: "text-amber-400", ring: "ring-amber-200" },
  lime: { bg: "bg-lime-50", border: "border-lime-200", text: "text-lime-700", textLight: "text-lime-400", ring: "ring-lime-200" },
  slate: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-700", textLight: "text-slate-400", ring: "ring-slate-200" },
  indigo: { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", textLight: "text-indigo-400", ring: "ring-indigo-200" },
  teal: { bg: "bg-teal-50", border: "border-teal-200", text: "text-teal-700", textLight: "text-teal-400", ring: "ring-teal-200" },
};

function ControlPanel({ isOpen, setIsOpen, activeTab, setActiveTab, onAddExpert }: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  activeTab: "modes" | "agents" | "reunion";
  setActiveTab: (v: "modes" | "agents" | "reunion") => void;
  onAddExpert: (code: string) => void;
}) {
  // Qualification flow state (modes)
  const [qualMode, setQualMode] = useState<typeof REFLEXION_MODES[0] | null>(null);
  const [qualParticipants, setQualParticipants] = useState<"solo" | "duo" | "equipe">("duo");
  const [qualExperts, setQualExperts] = useState<string[]>([]);
  const [qualSubject, setQualSubject] = useState("");
  // Qualification flow state (reunion)
  const [qualPlaybook, setQualPlaybook] = useState<typeof PLAYBOOKS[0] | null>(null);
  const [reunionSubject, setReunionSubject] = useState("");

  const { activeBotCode, setActivePhase, setReflexionContext, setRightSection, setActiveMeeting, setReflexionSetup } = useAmorcer();
  const { activeRoster, addBotToRoster, removeBotFromRoster, sendMessage, setReflectionMode } = useChatContext();

  const startQualification = useCallback((mode: typeof REFLEXION_MODES[0]) => {
    setQualMode(mode);
    setQualExperts([]);
    setQualSubject("");
    setQualParticipants("duo");
  }, []);

  const cancelQualification = useCallback(() => {
    setQualMode(null);
  }, []);

  const launchMode = useCallback(() => {
    if (!qualMode) return;
    // C.44 — propager le sujet au contexte réflexion pour éviter la question dupliquée dans LiveReflexionView
    if (qualSubject.trim()) setReflexionContext(qualSubject.trim());
    // FIX Bug1: activer le mode réflexion pour que WorkspacePhasesPanel route vers LiveReflexionView
    setReflectionMode(qualMode.id as ReflectionMode);
    // Route to workspace setup panel (ReflexionSetupPanel in LiveDiscussionView)
    setReflexionSetup({
      mode: qualMode.id,
      participants: qualParticipants,
      experts: qualExperts,
      subject: qualSubject.trim(),
    });
    setActivePhase("reflexion" as PhaseKey);
    setRightSection(null);
    setIsOpen(false);
    setQualMode(null);
  }, [qualMode, qualSubject, qualExperts, qualParticipants, setReflexionSetup, setActivePhase, setRightSection, setIsOpen, setReflexionContext, setReflectionMode]);

  const toggleExpert = useCallback((code: string) => {
    setQualExperts(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  }, []);

  const startPlaybookQualification = useCallback((pb: typeof PLAYBOOKS[0]) => {
    setQualPlaybook(pb);
    setReunionSubject("");
  }, []);

  const cancelPlaybookQualification = useCallback(() => {
    setQualPlaybook(null);
  }, []);

  const launchPlaybook = useCallback(() => {
    if (!qualPlaybook) return;
    const bots = qualPlaybook.bots.length > 0 ? qualPlaybook.bots : (activeRoster.length > 0 ? activeRoster : [activeBotCode]);
    bots.forEach(code => {
      if (!activeRoster.includes(code)) addBotToRoster(code);
    });
    setActiveMeeting({
      type: qualPlaybook.id,
      title: reunionSubject.trim() || `${qualPlaybook.label}: ${qualPlaybook.desc}`,
      botCodes: bots,
    });
    setRightSection(null);
    setIsOpen(false);
    setQualPlaybook(null);
  }, [qualPlaybook, reunionSubject, activeRoster, activeBotCode, addBotToRoster, setActiveMeeting, setRightSection, setIsOpen]);

  if (!isOpen) return null;

  return (
    <div className="shrink-0 bg-white border-t border-gray-200 flex flex-col">
      {/* No internal tab bar — toolbar buttons ARE the tabs. Just a close button. */}
      <div className="flex items-center justify-end px-3 pt-2">
        <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-gray-100 rounded cursor-pointer">
          <X className="h-3.5 w-3.5 text-gray-400" />
        </button>
      </div>

      {/* Tab content */}
      <div className="px-3 pb-3">
        {/* ═══ MODES DE REFLEXION ═══ */}
        {activeTab === "modes" && !qualMode && (
          <div>
            <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-2">Lancer un mode de reflexion</div>
            <div className="grid grid-cols-3 gap-1.5">
              {REFLEXION_MODES.map(mode => {
                const ms = MODE_BUTTON_STYLES[mode.color] || MODE_BUTTON_STYLES.amber;
                return (
                  <button
                    key={mode.id}
                    onClick={() => startQualification(mode)}
                    className={cn("flex items-center justify-center gap-1.5 px-2 py-2 rounded-xl text-[10px] font-medium border cursor-pointer transition-colors", ms.bg, ms.text, ms.border, ms.hover)}
                  >
                    <mode.icon className={cn("h-3.5 w-3.5 shrink-0", ms.iconColor)} />
                    {mode.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ QUALIFICATION FLOW — MODES (replaces grid when mode selected) ═══ */}
        {activeTab === "modes" && qualMode && (
          <div>
            <div className="px-3 py-2 -mx-3 -mt-0 bg-orange-50 border-b border-orange-100 flex items-center gap-2 mb-3">
              <qualMode.icon className="h-4 w-4 text-orange-600" />
              <span className="text-xs font-bold text-orange-700">{qualMode.label}</span>
              <button onClick={cancelQualification} className="ml-auto text-[9px] text-gray-400 hover:text-gray-600 cursor-pointer">
                ✕ Annuler
              </button>
            </div>
            <div className="space-y-3">
              {/* Q1: Participants */}
              <div>
                <div className="text-[10px] font-bold text-gray-600 mb-1.5">Qui participe?</div>
                <div className="flex gap-1.5">
                  {([
                    { key: "solo" as const, label: "Solo" },
                    { key: "duo" as const, label: "Duo (1 expert)" },
                    { key: "equipe" as const, label: "Equipe (2-4)" },
                  ]).map(p => (
                    <button
                      key={p.key}
                      onClick={() => setQualParticipants(p.key)}
                      className={cn(
                        "flex-1 px-2 py-1.5 rounded-lg text-[10px] font-medium border text-center cursor-pointer transition-colors",
                        qualParticipants === p.key
                          ? "border-orange-300 bg-orange-50 text-orange-700 ring-1 ring-orange-200"
                          : "border-gray-200 bg-white text-gray-700 hover:border-orange-300 hover:bg-orange-50"
                      )}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Q2: Experts — pills colored per bot */}
              {qualParticipants !== "solo" && (
                <div>
                  <div className="text-[10px] font-bold text-gray-600 mb-1.5">Quel(s) expert(s)?</div>
                  <div className="flex flex-wrap gap-1">
                    {BOT_LIST_CP.filter(b => b.code !== activeBotCode).map(bot => {
                      const isSelected = qualExperts.includes(bot.code);
                      const ps = BOT_PILL_STYLES[bot.colorName] || BOT_PILL_STYLES.sky;
                      return (
                        <button
                          key={bot.code}
                          onClick={() => toggleExpert(bot.code)}
                          className={cn(
                            "px-2 py-1 rounded-full text-[9px] font-medium border flex items-center gap-1 cursor-pointer transition-colors",
                            isSelected
                              ? `${ps.border} ${ps.bg} ${ps.text} ring-1 ${ps.ring}`
                              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                          )}
                        >
                          {(() => { const DeptIcon = DEPT_DASH_ICON[bot.code]; return DeptIcon ? <DeptIcon className={cn("h-3 w-3 shrink-0", ps.text)} /> : null; })()}
                          {bot.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Q3: Sujet */}
              <div>
                <div className="text-[10px] font-bold text-gray-600 mb-1.5">Sujet de la reflexion</div>
                <input
                  type="text"
                  value={qualSubject}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setQualSubject(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && launchMode()}
                  className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 bg-white outline-none"
                  placeholder="Decris le sujet..."
                />
              </div>
              {/* Launch button */}
              <button
                onClick={launchMode}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-orange-500 text-white text-xs font-bold hover:bg-orange-600 shadow-sm cursor-pointer transition-colors"
              >
                <Zap className="h-3.5 w-3.5" />
                Lancer →
              </button>
            </div>
          </div>
        )}

        {/* ═══ AGENTS ═══ */}
        {activeTab === "agents" && (
          <div className="space-y-3">
            {/* Section 1 — Bots actifs */}
            {activeRoster.length > 0 && (
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-2">Bots actifs</div>
                <div className="space-y-1.5">
                  {activeRoster.map(code => {
                    const bot = BOT_LIST_CP.find(b => b.code === code);
                    const ps = BOT_PILL_STYLES[bot?.colorName || "sky"] || BOT_PILL_STYLES.sky;
                    const avatarUrl = BOT_AVATAR[code];
                    return (
                      <div key={code} className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-xl border shadow-sm",
                        ps.bg, ps.border
                      )}>
                        <div className={cn("w-9 h-9 rounded-full shrink-0 overflow-hidden ring-2", ps.ring)}>
                          {avatarUrl ? (
                            <img src={avatarUrl} alt={bot?.name || code} className="w-full h-full object-cover" />
                          ) : (
                            <span className={cn("w-full h-full flex items-center justify-center text-white text-[10px] font-bold", bot?.color || "bg-sky-500")}>
                              {(bot?.name || code).substring(0, 2).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-gray-900">{bot?.name || code}</div>
                          <div className="text-[10px] text-gray-500">{bot?.role}</div>
                        </div>
                        <button onClick={() => removeBotFromRoster(code)} className="text-[10px] text-red-400 hover:text-red-600 cursor-pointer px-2 py-1 rounded-lg hover:bg-red-50 transition-colors">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Section 2 — Ajouter un expert — style tour de controle avec avatar photo */}
            <div>
              <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400 mb-2">Ajouter un expert</div>
              <div className="grid grid-cols-4 gap-2">
                {BOT_LIST_CP.filter(b => !activeRoster.includes(b.code) && b.code !== activeBotCode).map(bot => {
                  const avatarUrl = BOT_AVATAR[bot.code];
                  const ps = BOT_PILL_STYLES[bot.colorName] || BOT_PILL_STYLES.sky;
                  const gradientMap: Record<string, string> = {
                    sky: "from-sky-100 to-sky-200", violet: "from-violet-100 to-violet-200",
                    emerald: "from-emerald-100 to-emerald-200", pink: "from-pink-100 to-pink-200",
                    red: "from-red-100 to-red-200", orange: "from-orange-100 to-orange-200",
                    amber: "from-amber-100 to-amber-200", lime: "from-lime-100 to-lime-200",
                    slate: "from-slate-100 to-slate-200", indigo: "from-indigo-100 to-indigo-200",
                    teal: "from-teal-100 to-teal-200",
                  };
                  const shadowMap: Record<string, string> = {
                    sky: "group-hover:shadow-sky-200/60", violet: "group-hover:shadow-violet-200/60",
                    emerald: "group-hover:shadow-emerald-200/60", pink: "group-hover:shadow-pink-200/60",
                    red: "group-hover:shadow-red-200/60", orange: "group-hover:shadow-orange-200/60",
                    amber: "group-hover:shadow-amber-200/60", lime: "group-hover:shadow-lime-200/60",
                    slate: "group-hover:shadow-slate-200/60", indigo: "group-hover:shadow-indigo-200/60",
                    teal: "group-hover:shadow-teal-200/60",
                  };
                  return (
                    <button
                      key={bot.code}
                      onClick={() => { onAddExpert(bot.code); setIsOpen(false); }}
                      className={cn(
                        "group relative flex flex-col items-center rounded-xl overflow-hidden bg-white border border-gray-100 cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:shadow-lg",
                        shadowMap[bot.colorName] || "group-hover:shadow-gray-200/60"
                      )}
                    >
                      {/* Gradient banner top */}
                      <div className={cn("w-full h-5 bg-gradient-to-r", gradientMap[bot.colorName] || "from-sky-400 to-sky-600")} />
                      {/* Avatar overlapping the banner */}
                      <div className="w-11 h-11 rounded-full overflow-hidden border-2 border-white -mt-4 shadow-sm">
                        {avatarUrl ? (
                          <img src={avatarUrl} alt={bot.name} className="w-full h-full object-cover" />
                        ) : (
                          <span className={cn("w-full h-full flex items-center justify-center text-white text-[11px] font-bold", bot.color)}>
                            {bot.name.substring(0, 2).toUpperCase()}
                          </span>
                        )}
                      </div>
                      {/* Name + role — punchy style */}
                      <div className="text-center px-1 pt-1.5 pb-2.5">
                        <div className={cn("text-[11px] font-extrabold tracking-tight leading-none", ps.text)}>{bot.name}</div>
                        <div className="text-[8px] font-bold text-gray-400 uppercase tracking-wider leading-tight mt-0.5">{bot.role}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* ═══ REUNION ═══ */}
        {activeTab === "reunion" && !qualPlaybook && (
          <div>
            {/* Nouvelle reunion (libre) — top card dashed */}
            <button
              onClick={() => startPlaybookQualification(PLAYBOOKS[0])}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 border-dashed border-blue-300 bg-blue-50/50 hover:bg-blue-50 cursor-pointer transition-colors mb-3"
            >
              <div className="w-8 h-8 rounded-lg bg-white border border-blue-200 flex items-center justify-center shrink-0">
                <Video className="h-4 w-4 text-blue-500" />
              </div>
              <div className="flex-1 text-left">
                <div className="text-[10px] font-bold text-gray-800">Nouvelle reunion</div>
                <div className="text-[9px] text-gray-500">Format libre, choisissez vos participants</div>
              </div>
              <ChevronRight className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            </button>

            {/* Ou choisir un playbook */}
            <div className="text-[9px] font-bold text-gray-400 uppercase tracking-wider mb-2">Ou choisir un playbook</div>
            <div className="grid grid-cols-4 gap-1.5">
              {PLAYBOOKS.slice(1).map(pb => {
                const hoverMap: Record<string, string> = {
                  indigo: "hover:bg-indigo-50 hover:border-indigo-200",
                  emerald: "hover:bg-emerald-50 hover:border-emerald-200",
                  amber: "hover:bg-amber-50 hover:border-amber-200",
                  red: "hover:bg-red-50 hover:border-red-200",
                  sky: "hover:bg-sky-50 hover:border-sky-200",
                  gray: "hover:bg-gray-50 hover:border-gray-300",
                  pink: "hover:bg-pink-50 hover:border-pink-200",
                  orange: "hover:bg-orange-50 hover:border-orange-200",
                  cyan: "hover:bg-cyan-50 hover:border-cyan-200",
                  teal: "hover:bg-teal-50 hover:border-teal-200",
                  violet: "hover:bg-violet-50 hover:border-violet-200",
                  lime: "hover:bg-lime-50 hover:border-lime-200",
                  purple: "hover:bg-purple-50 hover:border-purple-200",
                  blue: "hover:bg-blue-50 hover:border-blue-200",
                  rose: "hover:bg-rose-50 hover:border-rose-200",
                  green: "hover:bg-green-50 hover:border-green-200",
                };
                const iconColorMap: Record<string, string> = {
                  indigo: "text-indigo-500", emerald: "text-emerald-500", amber: "text-amber-500",
                  red: "text-red-500", sky: "text-sky-500", gray: "text-gray-400",
                  pink: "text-pink-500", orange: "text-orange-500", cyan: "text-cyan-500",
                  teal: "text-teal-500", violet: "text-violet-500", lime: "text-lime-600",
                  purple: "text-purple-500", blue: "text-blue-500", rose: "text-rose-500",
                  green: "text-green-600",
                };
                return (
                  <button
                    key={pb.id}
                    onClick={() => startPlaybookQualification(pb)}
                    className={cn(
                      "flex flex-col items-center gap-1 px-1.5 py-2 rounded-lg border border-gray-200 bg-white cursor-pointer transition-colors text-center",
                      hoverMap[pb.color] || "hover:bg-gray-50 hover:border-gray-300"
                    )}
                  >
                    <pb.icon className={cn("h-4 w-4 shrink-0", iconColorMap[pb.color] || "text-gray-500")} />
                    <div className="text-[9px] font-bold text-gray-700 leading-tight">{pb.label}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ═══ QUALIFICATION FLOW — REUNION (replaces grid when playbook selected) ═══ */}
        {activeTab === "reunion" && qualPlaybook && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <qualPlaybook.icon className="h-4 w-4 text-gray-600" />
              <span className="text-xs font-bold text-gray-700">{qualPlaybook.label}</span>
              <button onClick={cancelPlaybookQualification} className="ml-auto text-[9px] text-gray-400 hover:text-gray-600 flex items-center gap-1 cursor-pointer">
                ← Retour
              </button>
            </div>
            <div className="space-y-3">
              {/* Flow stages pills — per-playbook numbered stages */}
              {qualPlaybook.stages.length > 0 && (
                <div>
                  <div className="text-[10px] font-bold text-gray-600 mb-1">Deroulement (playbook)</div>
                  <div className="flex gap-1 flex-wrap">
                    {qualPlaybook.stages.map((stage, i) => {
                      const isFirst = i === 0;
                      const isLast = i === qualPlaybook.stages.length - 1;
                      return (
                        <span
                          key={stage}
                          className={cn(
                            "px-2 py-0.5 rounded-full text-[8px] font-medium border",
                            isFirst ? "bg-sky-100 text-sky-700 border-sky-200"
                              : isLast ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                              : "bg-blue-50 text-blue-600 border-blue-100"
                          )}
                        >
                          {i + 1}. {stage}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Bots invites — colored per bot with role */}
              <div>
                <div className="text-[10px] font-bold text-gray-600 mb-1">Bots invites <span className="font-normal text-gray-400">(modifiable)</span></div>
                <div className="flex flex-wrap gap-1">
                  {qualPlaybook.bots.map(code => {
                    const bot = BOT_LIST_CP.find(b => b.code === code);
                    const ps = BOT_PILL_STYLES[bot?.colorName || "sky"] || BOT_PILL_STYLES.sky;
                    return (
                      <span key={code} className={cn("px-2 py-1 rounded-full text-[9px] font-medium border flex items-center gap-1", ps.bg, ps.text, ps.border)}>
                        {(() => { const DeptIcon = DEPT_DASH_ICON[code]; return DeptIcon ? <DeptIcon className="h-3 w-3 shrink-0" /> : null; })()}
                        {bot?.name || code} <span className={ps.textLight}>{bot?.role}</span>
                      </span>
                    );
                  })}
                  <button
                    onClick={() => { setActiveTab("agents"); }}
                    className="px-2 py-1 rounded-full text-[9px] font-medium border border-dashed border-gray-300 text-gray-400 hover:border-gray-400 cursor-pointer transition-colors"
                  >
                    + Ajouter
                  </button>
                </div>
              </div>
              {/* Sujet */}
              <div>
                <div className="text-[10px] font-bold text-gray-600 mb-1">Sujet de la reunion</div>
                <input
                  type="text"
                  value={reunionSubject}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setReunionSubject(e.target.value)}
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => e.key === "Enter" && launchPlaybook()}
                  className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-300 bg-white outline-none"
                  placeholder="Ex: Budget Q3, Expansion Ontario..."
                />
              </div>
              {/* Launch button — dark navy */}
              <button
                onClick={launchPlaybook}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#073E5A] text-white text-xs font-bold hover:opacity-90 shadow-sm cursor-pointer transition-colors"
              >
                <Video className="h-3.5 w-3.5" />
                Demarrer la reunion →
              </button>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}

// ═══ CHATBOX V3 — DESIGN MODÉLISÉ (Style Claude AI) + BACKEND BRANCHÉ ═══

type CallState = "idle" | "connecting" | "connected" | "error";

const formatCallDuration = (s: number) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

function ChatBoxV3({ onOpenPanel }: { onOpenPanel?: (tab: "modes" | "agents" | "reunion") => void }) {
  const [inputText, setInputText] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [showIntegrationsPanel, setShowIntegrationsPanel] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  // S231 lot Z2 (ZQ3 a) : « Le préciser » et « Demander une modification » de la carte d'action mettent le curseur dans le champ
  useEffect(() => {
    const focus = () => textareaRef.current?.focus();
    window.addEventListener(EVT_FOCUS_DISCUSSION, focus);
    return () => window.removeEventListener(EVT_FOCUS_DISCUSSION, focus);
  }, []);
  const attachRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // visionInputRef retiré — Vision = app mobile (Ray-Ban Meta)
  const { sendMessage, sendMultiPerspective, injectVoiceMessage, newConversation, chatTargetBot, activeRoster, setReflectionMode, activeThreadId, isTyping } = useChatContext();
  const { activeBotCode, activePhase, setRightSection, reflexionContext, setReflexionContext, setFocusType, setActivePhase, activeMeeting, chatStage, workspaceBlocks, ouvrirDiscussionDepuisZone } = useAmorcer();
  // setRightSection utilisé par "Connecteurs API" dans le menu + pour Drive picker
  // Enrichir activePhase avec le step CREDO pour que le backend injecte le bon prompt
  const _credoStepsCB = ["comprendre", "rechercher", "exposer", "demontrer", "objectif"];
  const workspacePhase = activePhase === "discussion" && chatStage < _credoStepsCB.length
    ? `discussion_${_credoStepsCB[chatStage]}`
    : activePhase;

  // ═══ VOICE CALL STATE ═══
  const [callState, setCallState] = useState<CallState>("idle");
  const [micOn, setMicOn] = useState(true);
  const [callDuration, setCallDuration] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState<{ file: File; previewUrl?: string; isImage: boolean } | null>(null);
  const roomRef = useRef<Room | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollCursorRef = useRef(0);
  const userHangupRef = useRef(false);
  const injectRef = useRef(injectVoiceMessage);
  injectRef.current = injectVoiceMessage;

  // Fermer menu attach si click extérieur
  useEffect(() => {
    if (!showAttachMenu) return;
    function handleClick(e: MouseEvent) {
      if (attachRef.current && !attachRef.current.contains(e.target as Node)) setShowAttachMenu(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showAttachMenu]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  // S229 lot G (DEC-POSER-QUESTION-1) : « Poser la question » d'une carte — la bulle de Carlos écrite par le serveur (route du geste)
  // s'affiche dans le chat avec ses options ; aucun message du client
  useEffect(() => {
    const montrer = (e: Event) => {
      const d = (e as CustomEvent<{ texte?: string; options?: string[] }>).detail || {};
      if (d.texte) injectRef.current("assistant", d.texte, "CEOB", { options: d.options || [] });
    };
    window.addEventListener(EVT_BULLE_POSER_QUESTION, montrer);
    return () => window.removeEventListener(EVT_BULLE_POSER_QUESTION, montrer);
  }, []);

  // ═══ MEETING TRANSCRIPT POLLING ═══
  const meetingPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const meetingCursorRef = useRef(0);

  useEffect(() => {
    // Quand activeMeeting a un slug → démarrer le polling transcript
    const slug = activeMeeting?.slug;
    if (!slug) {
      if (meetingPollRef.current) { clearInterval(meetingPollRef.current); meetingPollRef.current = null; }
      meetingCursorRef.current = 0;
      return;
    }
    meetingCursorRef.current = 0;
    meetingPollRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `/api/v1/meetings/${slug}/transcript?cursor=${meetingCursorRef.current}`,
          { headers: { "X-API-Key": import.meta.env.VITE_API_KEY || "" } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data.entries?.length > 0) {
          for (const entry of data.entries) {
            const role = entry.speaker === "user" ? "user" : "assistant";
            injectRef.current(role, entry.text, entry.agent || undefined, role === "assistant" ? {
              options: entry.options,
              canvasActions: entry.canvas_actions,
              teamProposal: entry.team_proposal,
              phaseCredo: entry.phase_credo,
              bubbleContext: entry.bubble_context,
              isDiagnostic: entry.is_diagnostic,
              ghostActif: entry.ghost_actif,
              cascadeSuggestions: entry.cascade_suggestions,
              scaffoldProgress: entry.scaffold_progress,
            } : undefined);
          }
          meetingCursorRef.current = data.cursor ?? (meetingCursorRef.current + data.entries.length);
        }
      } catch { /* retry next poll */ }
    }, 3000);
    return () => {
      if (meetingPollRef.current) { clearInterval(meetingPollRef.current); meetingPollRef.current = null; }
    };
  }, [activeMeeting?.slug]);

  // ═══ VOICE POLLING ═══
  const startVoicePolling = useCallback((roomName: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollCursorRef.current = 0;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(
          `/api/v1/voice/events/${roomName}?cursor=${pollCursorRef.current}`,
          { headers: { "X-API-Key": import.meta.env.VITE_API_KEY || "" } }
        );
        if (!res.ok) return;
        const data = await res.json();
        if (data.events?.length > 0) {
          for (const evt of data.events) {
            if (evt.type === "user_transcript") {
              // Transcript immediat — affiche le texte user des qu'il parle
              if (evt.user_text) injectRef.current("user", evt.user_text);
            } else if (evt.type === "exchange") {
              if (evt.user_text) injectRef.current("user", evt.user_text);
              if (evt.bot_text) injectRef.current("assistant", evt.bot_text, evt.agent, {
                options: evt.options,
                canvasActions: evt.canvas_actions,
                teamProposal: evt.team_proposal,
                phaseCredo: evt.phase_credo,
                bubbleContext: evt.bubble_context,
                isDiagnostic: evt.is_diagnostic,
                ghostActif: evt.ghost_actif,
                tier: evt.tier,
                latenceMs: evt.latence_ms,
                cascadeSuggestions: evt.cascade_suggestions,
                scaffoldProgress: evt.scaffold_progress,
                workspaceBlock: evt.workspace_block || undefined,  // C.52
              });
            }
          }
          pollCursorRef.current = data.cursor;
        }
      } catch { /* retry next poll */ }
    }, 800);
  }, []);

  const stopVoicePolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    pollCursorRef.current = 0;
  }, []);

  // ═══ END CALL ═══
  const endCall = useCallback(() => {
    userHangupRef.current = true;
    // Notify backend to cleanup voice events for this room
    const roomName = roomRef.current?.name;
    if (roomName) {
      fetch(`/api/v1/voice/end`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-API-Key": import.meta.env.VITE_API_KEY || "" },
        body: JSON.stringify({ room_name: roomName }),
      }).catch(() => {});
    }
    if (roomRef.current) { roomRef.current.disconnect(); roomRef.current = null; }
    if (audioElRef.current) { audioElRef.current.srcObject = null; audioElRef.current.remove(); audioElRef.current = null; }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    stopVoicePolling();
    setCallState("idle");
    setCallDuration(0);
    setMicOn(true);
  }, [stopVoicePolling]);

  // ═══ START CALL ═══
  const startCall = useCallback(async () => {
    if (callState === "connecting" || callState === "connected") return;
    setCallState("connecting");
    setCallDuration(0);
    // VOIX.THREAD.4 — garantir un discussion_id avant d'ouvrir la room LiveKit.
    // Si un fil est actif on l'utilise ; sinon on en cree un pour que l'agent
    // puisse persister l'echange (VOIX.THREAD.1 echouait faute de thread_id).
    const voiceThreadId = activeThreadId || newConversation();
    if (!voiceThreadId) {
      console.error("VOIX.THREAD.4: impossible de creer un fil — appel vocal annule");
      setCallState("idle");
      return;
    }

    // Mobile autoplay fix: warm up AudioContext during user gesture (synchronous, before any await)
    // iOS/Android require AudioContext.resume() within a tap/click context
    try {
      const _ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      if (_ctx.state === "suspended") _ctx.resume();
    } catch {}

    // Pre-create audio element during user gesture
    if (!audioElRef.current) {
      audioElRef.current = document.createElement("audio");
      audioElRef.current.autoplay = true;
      document.body.appendChild(audioElRef.current);
    }

    try {
      const tokenData = await api.voiceToken(activeBotCode, 1, false, voiceThreadId);
      const room = new Room({ adaptiveStream: true, dynacast: true, disconnectOnPageLeave: false });
      roomRef.current = room;

      room.on(RoomEvent.TrackSubscribed, (track: RemoteTrack, _pub: RemoteTrackPublication, _p: Participant) => {
        if (track.kind === Track.Kind.Audio) {
          if (!audioElRef.current) {
            audioElRef.current = document.createElement("audio");
            audioElRef.current.autoplay = true;
            (audioElRef.current as any).playsInline = true;
            document.body.appendChild(audioElRef.current);
          }
          track.attach(audioElRef.current);
          audioElRef.current.play().catch(() => {});
        }
      });

      room.on(RoomEvent.Disconnected, (reason?: DisconnectReason) => {
        if (userHangupRef.current) { userHangupRef.current = false; return; }
        if (reason !== undefined && reason !== 0) {
          setCallState("error");
          if (timerRef.current) clearInterval(timerRef.current);
          setTimeout(() => setCallState("idle"), 3000);
        } else {
          endCall();
        }
      });
      room.on(RoomEvent.Reconnecting, () => setCallState("connecting"));
      room.on(RoomEvent.Reconnected, () => setCallState("connected"));

      await room.connect(tokenData.livekit_url, tokenData.token);
      await room.localParticipant.setMicrophoneEnabled(true);
      // LiveKit native autoplay handler — enables audio playback on mobile browsers
      room.startAudio().catch(() => {});
      setCallState("connected");

      // Connection sound (A major chord)
      try {
        const ac = new AudioContext();
        const t = ac.currentTime;
        [440, 554, 659].forEach((freq) => {
          const o = ac.createOscillator(); const g = ac.createGain();
          o.type = "sine"; o.frequency.value = freq;
          o.connect(g); g.connect(ac.destination);
          g.gain.setValueAtTime(0, t);
          g.gain.linearRampToValueAtTime(0.08, t + 0.05);
          g.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
          o.start(t); o.stop(t + 0.9);
        });
      } catch { /* silent */ }

      startVoicePolling(tokenData.room_name);
      timerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
    } catch (err) {
      console.error("[ChatBoxV3] Voice connection failed:", err);
      setCallState("error");
      if (injectRef.current) {
        injectRef.current("system",
          "Connexion vocale échouée. Vérifiez votre micro et réessayez.",
          undefined, {}
        );
      }
      setTimeout(() => setCallState("idle"), 5000);
    }
  }, [activeBotCode, callState, endCall, startVoicePolling]);

  // ═══ TOGGLE MIC ═══
  const toggleMic = useCallback(async () => {
    if (roomRef.current && callState === "connected") {
      const next = !micOn;
      await roomRef.current.localParticipant.setMicrophoneEnabled(next);
      setMicOn(next);
    }
  }, [micOn, callState]);

  // ═══ VISION — Carlos Vision (Ray-Ban Meta / app mobile) ═══
  const [visionToast, setVisionToast] = useState(false);
  const [visionActive, setVisionActive] = useState(false);
  // P-S232-31 n° 7 et 11 : message court de l'écran (refus d'un geste, clic ou envoi pendant que Carlos écrit), 4 s dans le même bandeau
  const [messageEcran, setMessageEcran] = useState<string | null>(null);
  useEffect(() => {
    let minuteur: ReturnType<typeof setTimeout> | null = null;
    const h = (e: Event) => {
      const t = (e as CustomEvent).detail?.texte; if (!t) return;
      setMessageEcran(String(t)); if (minuteur) clearTimeout(minuteur); minuteur = setTimeout(() => setMessageEcran(null), 4000);
    };
    window.addEventListener(EVT_MESSAGE_ECRAN, h);
    return () => { window.removeEventListener(EVT_MESSAGE_ECRAN, h); if (minuteur) clearTimeout(minuteur); };
  }, []);
  const handleVision = useCallback(() => {
    // Mobile: deep link vers l'APK CarlOS Vision + polling transcripts
    const isMobileDevice = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (isMobileDevice) {
      window.location.href = "cameraaccess://launch";
    }
    // Start polling vision room transcripts (mobile + desktop pour la demo)
    startVoicePolling("carlos-vision-rayban");
    setVisionActive(true);
  }, [startVoicePolling]);

  // ═══ FILE UPLOAD — sélection locale → preview chip, upload au Send ═══
  const handleFileUpload = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setShowAttachMenu(false);
    const isImage = file.type.startsWith("image/");
    const previewUrl = isImage ? URL.createObjectURL(file) : undefined;
    setPendingFile({ file, previewUrl, isImage });
  }, []);

  const removePendingFile = useCallback(() => {
    setPendingFile(prev => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  }, []);

  // ═══ TEXT HANDLERS ═══
  const handleSend = async () => {
    const text = inputText.trim();
    if (!text && !pendingFile) return;
    // P-S232-31 n° 2 (REVUE-NUIT : double envoi, question reposée, [DOSSIER] perdu) : rien ne part pendant que Carlos écrit (Entrée et bouton)
    if (isTyping) { signalerEcran("Carlos répond encore — attendez la fin de sa réponse."); return; }
    setInputText("");

    // FIX Lacune 1 + Sprint 3: Entrer en Discussion AVANT le sendMessage pour que workspacePhase
    // soit "discussion_comprendre" des le premier message (au lieu de "observation")
    let effectivePhase = workspacePhase;
    if (!reflexionContext) {
      setReflexionContext((text || pendingFile?.file.name || "").substring(0, 80));
    }
    // Sprint 3 fix: si workspacePhase est falsy ou incohérent avec l'URL discussion, corriger
    // ECRAN.COCKPIT.DISCUSSION.1 : phase, panneau de droite et adresse posés ensemble par un seul chemin (AmorcerContext.ouvrirDiscussion),
    // aussi quand la phase gardée est attention / moderation / operations ou que le fil était déjà actif sous le Cockpit
    const _isDiscURL = window.location.pathname.includes("/discussion/");
    if (!effectivePhase || effectivePhase === "observation" || (_isDiscURL && !effectivePhase.startsWith("discussion"))) {
      ouvrirDiscussionDepuisZone(activeThreadId, "discussion" as PhaseKey);
      effectivePhase = `discussion_${_credoStepsCB[chatStage] || "comprendre"}`;
    } else if (ouvrirDiscussionDepuisZone(activeThreadId) === "discussion" && !effectivePhase.startsWith("discussion")) {
      effectivePhase = `discussion_${_credoStepsCB[chatStage] || "comprendre"}`;
    }

    // Si un fichier est en attente — upload au moment du Send
    if (pendingFile) {
      const fileCopy = pendingFile;
      setPendingFile(null);
      if (fileCopy.previewUrl) URL.revokeObjectURL(fileCopy.previewUrl);
      setUploading(true);
      try {
        const docExts = [".pdf", ".docx", ".doc", ".txt", ".md", ".rtf"];
        const ext = fileCopy.file.name.substring(fileCopy.file.name.lastIndexOf(".")).toLowerCase();
        const isDocument = docExts.includes(ext);
        // DocForge restructuring uniquement en phase Conception — pas en Discussion
        if (isDocument && activePhase === "creation") {
          const formData = new FormData();
          formData.append("file", fileCopy.file);
          formData.append("bot_code", chatTargetBot);
          const res = await fetch("/api/v1/workspace/upload-restructure", {
            method: "POST",
            headers: { "X-API-Key": import.meta.env.VITE_API_KEY || "" },
            body: formData,
          });
          if (res.ok) {
            const data = await res.json();
            const msg = text ? `${text}\n\n[Document: ${fileCopy.file.name}]` : `Document: ${fileCopy.file.name}`;
            sendMessage(msg, chatTargetBot, undefined, { workspacePhase: effectivePhase, workspaceExpertContext: buildExpertContext(workspaceBlocks, activeBotCode) });
            if (data.sections?.length) {
              window.dispatchEvent(new CustomEvent("bt-start-deliverable", {
                detail: { deliverableType: "docforge_section", sections: data.sections, fileName: fileCopy.file.name },
              }));
            }
          } else {
            const result = await api.uploadBureauFile(fileCopy.file, fileCopy.file.name);
            const msg = text ? `${text}\n\n[Fichier joint: ${result.titre || fileCopy.file.name}]` : `Fichier joint: ${result.titre || fileCopy.file.name}`;
            sendMessage(msg, chatTargetBot, undefined, { workspacePhase: effectivePhase, workspaceExpertContext: buildExpertContext(workspaceBlocks, activeBotCode) });
          }
        } else if (isDocument) {
          // Document en Discussion — extraire le texte et l'inclure dans le message pour le bot
          const formData = new FormData();
          formData.append("file", fileCopy.file);
          formData.append("bot_code", chatTargetBot);
          const res = await fetch("/api/v1/workspace/upload-restructure", {
            method: "POST",
            headers: { "X-API-Key": import.meta.env.VITE_API_KEY || "" },
            body: formData,
          });
          if (res.ok) {
            const data = await res.json();
            // Reconstruire le texte depuis les sections pour donner contexte au bot
            const docText = (data.sections as any[] || [])
              .map((s: any) => s.content || "")
              .join("\n\n");
            const contextSnippet = docText
              ? `\n\n📄 Contenu de "${data.title || fileCopy.file.name}":\n${docText.substring(0, 5000)}`
              : "";
            const msg = text ? `${text}${contextSnippet}` : `📄 ${data.title || fileCopy.file.name}${contextSnippet}`;
            sendMessage(msg, chatTargetBot, undefined, { workspacePhase: effectivePhase, workspaceExpertContext: buildExpertContext(workspaceBlocks, activeBotCode) });
          } else {
            // Fallback si extraction échoue
            const result = await api.uploadBureauFile(fileCopy.file, fileCopy.file.name);
            const msg = text ? `${text}\n\n[Fichier joint: ${result.titre || fileCopy.file.name}]` : `Fichier joint: ${result.titre || fileCopy.file.name}`;
            sendMessage(msg, chatTargetBot, undefined, { workspacePhase: effectivePhase, workspaceExpertContext: buildExpertContext(workspaceBlocks, activeBotCode) });
          }
        } else {
          // Fichier non-document (image, etc.) — upload bureau simple
          const result = await api.uploadBureauFile(fileCopy.file, fileCopy.file.name);
          const msg = text ? `${text}\n\n[Fichier joint: ${result.titre || fileCopy.file.name}]` : `Fichier joint: ${result.titre || fileCopy.file.name}`;
          sendMessage(msg, chatTargetBot, undefined, { workspacePhase: effectivePhase, workspaceExpertContext: buildExpertContext(workspaceBlocks, activeBotCode) });
        }
      } catch (err) {
        console.error("[ChatBoxV3] Upload error:", err);
        // Fallback — ne pas perdre le prompt si l'upload échoue
        const fallbackMsg = text ? `${text}\n\n[Pièce jointe: ${fileCopy.file.name}]` : `Pièce jointe: ${fileCopy.file.name}`;
        sendMessage(fallbackMsg, chatTargetBot, undefined, { workspacePhase: effectivePhase });
      } finally {
        setUploading(false);
      }
    } else {
      // Slash commands — /brainstorm, /5pourquoi, /scamper, /analyse, /debat, /decision, /strategie
      const SLASH_MODES: Record<string, string> = {
        brainstorm: "brainstorm",
        "5pourquoi": "5-pourquoi",
        "5-pourquoi": "5-pourquoi",
        scamper: "scamper",
        analyse: "analyse",
        debat: "debat",
        decision: "decision",
        strategie: "strategie",
        credo: "credo",
      };
      const slashMatch = text.match(/^\/(\w[\w-]*)\s+([\s\S]+)/s);
      let msgText = text;
      if (slashMatch) {
        const modeKey = slashMatch[1].toLowerCase();
        const mapped = SLASH_MODES[modeKey];
        if (mapped) {
          setReflectionMode(mapped as any);
          msgText = slashMatch[2].trim();
        }
      }
      // W.1: Discussion 1:1 — toujours single-bot (experts dans le workspace)
      // C.50 — Passer les 5 derniers blocs pour CREATE/ENRICH/MERGE
      const _wsCtx50 = workspaceBlocks.slice(-10).map(b => ({ id: b.id, title: (b.title || "").slice(0, 60), type: b.type, summary: (b.summary || "").slice(0, 200) }));
      sendMessage(msgText, chatTargetBot, undefined, { workspacePhase: effectivePhase, workspaceExpertContext: buildExpertContext(workspaceBlocks, activeBotCode), workspaceBlocksContext: _wsCtx50.length > 0 ? _wsCtx50 : undefined });
    }

    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isInCall = callState === "connected" || callState === "connecting";
  const botName = BOT_NAME[chatTargetBot] || BOT_NAME[activeBotCode] || "CarlOS";

  return (
    <div className="shrink-0 bg-white px-3 pb-2 pt-1">
      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileUpload} />
      {/* Panneau connecteurs API */}
      {showIntegrationsPanel && (
        <IntegrationsPanel onClose={() => setShowIntegrationsPanel(false)} />
      )}
      {/* Vision toast — disponible dans l'app mobile */}
      {visionToast && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-4 py-2 rounded-full shadow-lg z-30 whitespace-nowrap">
          Ouvre l&apos;app CarlOS Vision sur ton téléphone
        </div>
      )}
      {/* P-S232-31 n° 7 et 11 : message court de l'écran, même bandeau que Vision */}
      {messageEcran && !visionToast && (
        <div role="status" data-message-ecran className="absolute bottom-24 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-xs px-4 py-2 rounded-full shadow-lg z-30 whitespace-nowrap">
          {messageEcran}
        </div>
      )}

      {/* Inline call bar — visible pendant un appel vocal */}
      {isInCall && (
        <div className={cn(
          "flex items-center gap-2 rounded-xl px-3 py-2 mb-1.5 shadow-sm",
          callState === "connecting" ? "bg-amber-50 border border-amber-200" : "bg-blue-50 border border-blue-200"
        )}>
          {callState === "connecting" ? (
            <Loader2 className="h-3.5 w-3.5 text-amber-600 animate-spin" />
          ) : (
            <Phone className="h-3.5 w-3.5 text-blue-600" />
          )}
          <span className="text-xs font-medium text-gray-700 flex-1">
            {callState === "connecting" ? `Connexion à ${botName}...` : `Appel avec ${botName}`}
          </span>
          {callState === "connected" && (
            <span className="text-xs font-mono text-gray-500">{formatCallDuration(callDuration)}</span>
          )}
          {callState === "connected" && (
            <button onClick={toggleMic} className={cn(
              "p-1.5 rounded-lg transition-colors cursor-pointer",
              micOn ? "text-gray-500 hover:bg-gray-100" : "bg-red-100 text-red-600"
            )} title={micOn ? "Couper le micro" : "Activer le micro"}>
              {micOn ? <Mic className="h-3.5 w-3.5" /> : <MicOff className="h-3.5 w-3.5" />}
            </button>
          )}
          <button onClick={endCall} className="p-1.5 rounded-lg bg-red-100 text-red-600 hover:bg-red-200 transition-colors cursor-pointer" title="Raccrocher">
            <PhoneOff className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      <div className="relative rounded-2xl border border-gray-200 bg-gradient-to-b from-white to-gray-50/50 shadow-sm focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100/80 focus-within:shadow-md transition-all">
        {/* Fichier en attente — preview avant envoi */}
        {pendingFile && (
          <div className="px-3 pt-2.5 pb-1 flex items-center gap-2">
            {pendingFile.isImage && pendingFile.previewUrl ? (
              <img
                src={pendingFile.previewUrl}
                alt={pendingFile.file.name}
                className="h-14 w-14 rounded-lg object-cover border border-gray-200 shrink-0"
              />
            ) : (
              <div className="flex items-center gap-2 bg-gray-100 rounded-lg px-2.5 py-1.5 max-w-[180px]">
                <FileText className="h-3.5 w-3.5 text-gray-500 shrink-0" />
                <span className="text-[11px] text-gray-700 truncate">{pendingFile.file.name}</span>
              </div>
            )}
            <button
              onClick={removePendingFile}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              title="Retirer"
            >
              <X className="h-3 w-3 text-gray-400" />
            </button>
          </div>
        )}
        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Parle a ${botName}...`}
          className="w-full text-[13px] leading-relaxed px-4 pt-3.5 pb-2 rounded-t-2xl border-0 focus:outline-none min-h-[72px] resize-none bg-transparent placeholder:text-gray-400"
          rows={3}
        />
        {/* Barre de boutons intégrée en bas de la box */}
        <div className="flex items-center gap-1 px-2 pb-2">
          {/* Menu + (pièce jointe, Drive, GitHub, connecteurs) */}
          <div className="relative" ref={attachRef}>
            <button
              onClick={() => setShowAttachMenu(!showAttachMenu)}
              className={cn("p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer", uploading && "pointer-events-none")}
              title="Ajouter"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            </button>
            {showAttachMenu && (
              <div className="absolute bottom-full left-0 mb-1 w-52 bg-white rounded-xl border border-gray-200 shadow-lg py-1 z-20">
                <button onClick={() => { setShowAttachMenu(false); setTimeout(() => fileInputRef.current?.click(), 10); }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors cursor-pointer text-left">
                  <Paperclip className="h-4 w-4 text-gray-500" />
                  <span className="text-xs text-gray-700">Pièce jointe</span>
                </button>
                <div className="border-t border-gray-100 my-1" />
                <button onClick={() => { setShowAttachMenu(false); setShowIntegrationsPanel(true); }} className="w-full flex items-center gap-3 px-3 py-2 hover:bg-gray-50 transition-colors cursor-pointer text-left">
                  <Activity className="h-4 w-4 text-indigo-500" />
                  <div>
                    <span className="text-xs text-gray-700">Connecteurs API</span>
                    <span className="block text-xs text-gray-400">Slack, HubSpot, GitHub, Stripe...</span>
                  </div>
                </button>
              </div>
            )}
          </div>

          {/* Toolbar compact: Discussion / Réunion / Vision | Réflexions / Agents */}
          <button
            onClick={isInCall ? endCall : startCall}
            className={cn(
              "flex items-center gap-1 px-1.5 py-1 rounded-md text-[10px] font-medium transition-all cursor-pointer",
              isInCall ? "bg-red-50 text-red-600 hover:bg-red-100" : "bg-blue-50 text-blue-600 hover:bg-blue-100"
            )}
            title={isInCall ? "Raccrocher" : "Discussion vocale"}
          >
            {callState === "connecting" ? <Loader2 className="h-3 w-3 animate-spin" /> : isInCall ? <PhoneOff className="h-3 w-3" /> : <Phone className="h-3 w-3" />}
            {isInCall ? "Raccrocher" : "Discussion"}
          </button>
          <button
            onClick={() => onOpenPanel?.("reunion")}
            className="flex items-center gap-1 px-1.5 py-1 rounded-md text-[10px] font-medium transition-all cursor-pointer bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
            title="Réunion AI"
          >
            <Video className="h-3 w-3" />Reunion
          </button>
          <button
            onClick={visionActive ? () => { stopVoicePolling(); setVisionActive(false); } : handleVision}
            className={cn(
              "flex items-center gap-1 px-1.5 py-1 rounded-md text-[10px] font-medium transition-all cursor-pointer",
              visionActive ? "bg-cyan-600 text-white hover:bg-cyan-700" : "bg-cyan-50 text-cyan-600 hover:bg-cyan-100"
            )}
            title={visionActive ? "Arrêter Vision" : "Vision Ray-Ban"}
          >
            <Glasses className="h-3 w-3" />{visionActive ? "Vision ON" : "Vision"}
          </button>
          <div className="w-px h-4 bg-gray-200" />
          {/* B1.ECRAN.5: bouton Modes masqué — MODES_VISIBLE=false
          <button onClick={() => onOpenPanel?.("modes")}
            className="flex items-center gap-1 px-1.5 py-1 rounded-md text-[10px] font-medium transition-all cursor-pointer bg-orange-50 text-orange-600 hover:bg-orange-100"
            title="Modes de réflexion"><Brain className="h-3.5 w-3.5" />Modes</button>
          */}
          <button
            onClick={() => onOpenPanel?.("agents")}
            className="flex items-center gap-1 px-1.5 py-1 rounded-md text-[10px] font-medium transition-all cursor-pointer bg-orange-50 text-orange-600 hover:bg-orange-100"
            title="Agents impliqués"
          >
            <Bot className="h-3 w-3" />+ Agents
          </button>

          <div className="flex-1" />

          {/* Bouton Envoyer — apparait quand il y a du texte */}
          <button
            onClick={handleSend}
            className={cn(
              "p-2.5 rounded-xl transition-all cursor-pointer",
              inputText.trim()
                ? "bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg active:scale-95"
                : "bg-gray-100 text-gray-300 cursor-default"
            )}
            title="Envoyer"
            disabled={!inputText.trim() || isTyping}
            data-envoi-bloque={isTyping ? "1" : undefined}
          >
            {inputText.trim() ? <ChevronUp className="h-4 w-4" /> : <Send className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {/* Disclaimer */}
      <p className="text-center text-xs text-gray-400 mt-1.5">
        Brain Team est une équipe d&apos;agents IA et peut faire des erreurs. Veuillez vérifier les réponses.
      </p>
    </div>
  );
}

